import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  buildDestinationConnectionCheckMetadata,
  buildDestinationDraft,
  buildDestinationFormMetadata,
  createClientGuid,
  ensureDestinationDefaults
} from "../components/wizard/steps/destinationSetupStepUtils";
import { buildStatusUrl, resolveClientShortNameAsync } from "../components/wizard/steps/EndpointReadinessStep";
import {
  buildDestinationDeploymentDraft,
  buildDestinationEntityDeploymentDraft,
  buildDestinationManualRunMetadata,
  buildWarehouseCloneMetadata,
  buildWarehouseDeployMetadata,
  buildWarehouseDropMetadata,
  DESTINATION_MANUAL_RUN_PROCESSOR_TYPES,
  DESTINATION_WAREHOUSE_DEPLOYMENT_OPERATIONS,
  firstItem,
  getArray,
  getBoolean,
  getDeploymentDisplayName,
  getDeploymentId,
  getDeploymentMatchKey,
  getDeploymentRenderKey,
  getDeploymentStateFieldName,
  getDeploymentTargetFieldName,
  getDeploymentTargetValue,
  getDestinationActiveSchemaVersionId,
  getDestinationDeployments,
  getDestinationEntityDeployments,
  getEndpointDeploymentSchemaScopes,
  getDestinationManualRunInitialValues,
  getDestinationManualRunOperationName,
  getDestinationManualRunSelectedMode,
  getDestinationSchemaId,
  getDisplayName,
  getEntityDeploymentMatchKey,
  getId,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  getOperationResultDebugDetails,
  getOperationResultDetails,
  getOperationResultMessage,
  getSchemaVersionLabel,
  getString,
  getValue,
  isEndpointDeploymentBasicOnly,
  isEndpointDeploymentMcpOnly,
  isRecordActive,
  isRecordDeleted,
  normalizeCompare,
  normalizeDestinationManualRunProcessorType,
  normalizeDestinationSavePayload,
  normalizeDestinationStateValue,
  normalizeFilePackagingModeValue,
  toDestinationSetupDefaults,
  updateDestinationDeploymentCollection,
  updateDestinationEntityDeploymentCollection,
  validateDestinationManualRunValues,
  validateWarehouseDeployValues,
  withDeploymentConnectionCheckId
} from "./resourceWorkspace.destinationUtils";

const getUniqueRowsByKey = (rows, getKey) => {
  const seen = new Set();
  return getArray({ rows }, "rows").filter((row, index) => {
    const key = String(getKey?.(row, index) || `row-${index}`).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getEntityObjectSourceObjectId = (entityObject) =>
  getString(
    entityObject,
    "SourceObjectOfRecordId",
    "sourceObjectOfRecordId",
    "SourceObjectOfRecordID",
    "sourceObjectOfRecordID",
    "SourceObjectId",
    "sourceObjectId",
    "SourceObjectID"
  );

const DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE = DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.realTime;

const createDestinationManualRunFeedbackState = () => ({
  active: false,
  complete: false,
  success: null,
  message: "",
  details: [],
  streamStarted: false,
  streamError: ""
});

const getSourceObjectComparableKeys = (sourceObject, index = 0) => [
  getId(sourceObject),
  getString(sourceObject, "SourceObjectId", "sourceObjectId", "SourceObjectID", "EntityObjectID", "EntityObjectId", "entityObjectId"),
  getString(sourceObject, "UniqueId", "uniqueId"),
  getString(sourceObject, "ObjectName", "objectName", "SystemName", "systemName"),
  `source-object-${index}`
].map(normalizeCompare).filter(Boolean);

const getSelectedSourceObjectsForEntityObjects = (entityObjects, sourceObjects) => {
  const sourceObjectIds = new Set(getArray({ entityObjects }, "entityObjects")
    .map(getEntityObjectSourceObjectId)
    .map(normalizeCompare)
    .filter(Boolean));
  if (!sourceObjectIds.size) return [];
  return getUniqueRowsByKey(
    getArray({ sourceObjects }, "sourceObjects")
      .filter((sourceObject, index) => getSourceObjectComparableKeys(sourceObject, index).some((key) => sourceObjectIds.has(key))),
    (sourceObject, index) => getSourceObjectComparableKeys(sourceObject, index)[0] || `source-object-${index}`
  );
};

const getEndpointWarehouseIds = (destination, defaults) =>
  getUniqueRowsByKey(
    getDestinationDeployments(destination, defaults)
      .map((deployment) => getString(deployment, "WarehouseId", "warehouseId", "wId", "wid"))
      .filter(Boolean)
      .map((warehouseId) => ({ warehouseId })),
    (row) => normalizeCompare(row.warehouseId)
  ).map((row) => row.warehouseId);

const emptyDestinationState = {
  linkedRecordId: "",
  destination: null,
  metadata: null,
  deploymentMetadata: null,
  entityDeploymentMetadata: null,
  schema: null,
  entityObjects: [],
  sourceObjects: [],
  warehouseDestinations: [],
  defaults: null,
  loading: false,
  error: ""
};

const DESTINATION_DEPLOYMENT_METADATA_NAMES = {
  warehouse: ["WarehouseDeployment", "SchemaDestinationDeployment"],
  fileStorage: ["FileStorageDeployment", "StorageDeployment", "SchemaDestinationDeployment"],
  externalSystem: ["ExternalSystemDeployment", "SchemaDestinationDeployment"],
  schemaAPI: ["SchemaAPIDeployment", "SchemaDestinationDeployment"]
};

const resolveMetadataByName = (metadataItems, names) => {
  const normalizedNames = new Set(getArray({ names }, "names").map(normalizeCompare).filter(Boolean));
  if (!normalizedNames.size) return null;
  return getArray({ metadataItems }, "metadataItems").find((item) => {
    const itemNames = [
      getString(item, "Name", "name"),
      getString(item, "ItemName", "itemName"),
      getString(item, "DisplayName", "displayName")
    ].map(normalizeCompare).filter(Boolean);
    return itemNames.some((name) => normalizedNames.has(name));
  }) || null;
};

const resolveDestinationDeploymentMetadata = (metadataItems, defaults) =>
  resolveMetadataByName(metadataItems, DESTINATION_DEPLOYMENT_METADATA_NAMES[defaults?.managerKey] || ["SchemaDestinationDeployment"]);

const resolveEntityDeploymentMetadata = (metadataItems) =>
  resolveMetadataByName(metadataItems, ["EntityObjectDeployment", "Entity Deployments", "Entity Deployment Scope"]);

const collapseAddEndpointMetadataTabs = (metadata) => {
  const fields = getArray(metadata, "Fields", "fields");

  return {
    ...metadata,
    Fields: fields.map((field) => {
      const fieldName = normalizeCompare(getString(field, "FieldName", "fieldName"));
      const isInformationField = fieldName === "name" || fieldName === "description";
      return {
        ...field,
        TabId: "Endpoint|1",
        GroupId: isInformationField ? "Information|1" : "Configuration|2"
      };
    })
  };
};

const getDefaultDeployment = (deployments) =>
  getArray({ deployments }, "deployments").find((deployment) => !isRecordDeleted(deployment) && isRecordActive(deployment))
  || getArray({ deployments }, "deployments").find((deployment) => !isRecordDeleted(deployment))
  || null;

const getDefaultDestinationDeployment = (destination, defaults) =>
  getDefaultDeployment(getDestinationDeployments(destination, defaults));

const readStatusResponse = async (response) => {
  const rawText = await response.text().catch(() => "");
  if (!rawText) return { payload: null, formatted: "" };
  try {
    const payload = JSON.parse(rawText);
    return { payload, formatted: JSON.stringify(payload, null, 2) };
  } catch {
    return { payload: { body: rawText }, formatted: rawText };
  }
};

const buildEndpointStatusDetails = ({ statusUrl, response, payload, formatted }) => {
  const api = payload?.api || payload?.Api || {};
  const auth = payload?.authentication || payload?.Authentication || {};
  return [
    `Status URL: ${statusUrl}`,
    `HTTP status: ${response.status} ${response.statusText || ""}`.trim(),
    `Endpoint online: ${payload?.endpointOnline ?? payload?.EndpointOnline ?? "Not set"}`,
    `Hosted API: ${api.hostedApiEnabled ?? api.HostedApiEnabled ?? "Not set"}`,
    `MCP: ${api.mcpEnabled ?? api.McpEnabled ?? "Not set"}`,
    `Auth mode: ${auth.authenticationMode ?? auth.AuthenticationMode ?? "Not set"}`,
    `Auth required: ${auth.authenticationRequired ?? auth.AuthenticationRequired ?? "Not set"}`,
    formatted ? `Status JSON:\n${formatted}` : ""
  ].filter(Boolean);
};

const buildEndpointSecureStatusUrl = (statusUrl) =>
  statusUrl ? statusUrl.replace(/\/status\/?$/i, "/secure-status") : "";

const getLifecycleOperationValue = (response) => {
  const result = getOperationResult(response);
  const value = result?.Value ?? result?.value;
  return value && typeof value === "object" ? value : {};
};

const getLifecycleOperationDetails = (response) => {
  const value = getLifecycleOperationValue(response);
  return [
    ...getOperationResultDetails(response),
    ...getArray(value, "failures", "Failures").map((detail) => `Failure: ${detail}`),
    ...getArray(value, "warnings", "Warnings").map((detail) => `Warning: ${detail}`),
    ...getArray(value, "diagnostics", "Diagnostics")
  ].filter(Boolean);
};

const isOperationSuccessful = (response) => {
  const result = getOperationResult(response);
  const value = getLifecycleOperationValue(response);
  return !(result?.Success === false || result?.success === false || value?.success === false || value?.Success === false);
};

const readEndpointJsonRequest = async (activeDestinationMgr, url) => {
  const axiosInstance = activeDestinationMgr?._itemService?._createAxiosInstance?.();
  if (axiosInstance) {
    try {
      const response = await axiosInstance.get(url);
      const payload = response?.data ?? null;
      return {
        ok: response?.status >= 200 && response?.status < 300,
        status: response?.status,
        statusText: response?.statusText || "OK",
        payload,
        formatted: payload ? JSON.stringify(payload, null, 2) : ""
      };
    } catch (requestError) {
      const response = requestError?.response;
      const payload = response?.data ?? { message: requestError?.message || "Endpoint secure status check failed." };
      return {
        ok: false,
        status: response?.status || "",
        statusText: response?.statusText || requestError?.message || "Request failed",
        payload,
        formatted: payload ? JSON.stringify(payload, null, 2) : ""
      };
    }
  }

  const response = await fetch(url, { method: "GET" });
  const { payload, formatted } = await readStatusResponse(response);
  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText || "",
    payload,
    formatted
  };
};

const getEndpointEntityEndpointKey = (row, index = 0) =>
  getString(row, "UniqueId", "uniqueId", "_id", "id", "_tempKey")
  || [
    getString(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "__DeploymentId"),
    getString(row, "EntityObjectId", "entityObjectId"),
    getString(row, "EntityName", "entityName", "DisplayName", "displayName")
  ].map(normalizeCompare).filter(Boolean).join(":")
  || `endpoint-entity-${index}`;

const cleanEndpointEntityEndpointRowForSave = (row) => {
  const cleanRow = { ...(row || {}) };
  delete cleanRow.__SchemaScopeSelectionKey;
  delete cleanRow.__EndpointEntityObjectSelectionId;
  delete cleanRow.__DeploymentId;
  delete cleanRow.__DeploymentName;
  delete cleanRow.__EndpointEntityMode;
  delete cleanRow.__EndpointEntityOriginalKey;
  delete cleanRow._tempKey;
  delete cleanRow.Text;
  delete cleanRow.Value;
  delete cleanRow.Disabled;
  return cleanRow;
};

const getEntityObjectRecordId = (entityObject) =>
  getString(entityObject, "_id", "Id", "id", "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId");

const getEntityObjectRouteName = (entityObject) =>
  getString(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "InternalName", "internalName", "Name", "name");

const DESTINATION_SAVE_RAW_RESPONSE_FIELD = "__destinationSaveRawResponse";

const attachRawDestinationSaveResponse = (normalizedResponse, rawResponse) => {
  if (!normalizedResponse || !rawResponse || normalizedResponse === rawResponse || typeof normalizedResponse !== "object") {
    return normalizedResponse;
  }
  try {
    Object.defineProperty(normalizedResponse, DESTINATION_SAVE_RAW_RESPONSE_FIELD, {
      value: rawResponse,
      enumerable: false,
      configurable: true
    });
  } catch {
    normalizedResponse[DESTINATION_SAVE_RAW_RESPONSE_FIELD] = rawResponse;
  }
  return normalizedResponse;
};

const getRawDestinationSaveResponse = (response) =>
  response?.[DESTINATION_SAVE_RAW_RESPONSE_FIELD] || response;

const getDestinationSaveOperationResponse = (response) => {
  const rawResponse = getRawDestinationSaveResponse(response);
  if (rawResponse?.data !== undefined) return rawResponse.data;
  if (response?.data !== undefined) return response.data;
  return response;
};

const hasBackendValidationErrors = (validationErrors) => {
  if (!validationErrors) return false;
  if (Array.isArray(validationErrors)) return validationErrors.length > 0;
  if (typeof validationErrors === "object") {
    return Object.keys(validationErrors).some((key) => key !== "__updatedDate" && validationErrors[key]);
  }
  return Boolean(validationErrors);
};

const getBackendValidationErrorsFromResponse = (response) => {
  const operationResponse = getDestinationSaveOperationResponse(response);
  const result = getOperationResult(operationResponse);
  return operationResponse?.ValidationErrors
    || operationResponse?.validationErrors
    || result?.ValidationErrors
    || result?.validationErrors
    || response?.data?.ValidationErrors
    || response?.data?.validationErrors;
};

export const useDestinationWorkspaceController = ({
  isDestinationConfigurationScreen,
  profileContextReady,
  selectedDestinationId,
  selectedDestinationDefaults,
  coreContext,
  schemaMgrRef,
  entityObjectMgrRef,
  sourceObjectMgrRef,
  clientMgrRef,
  warehouseMgrRef,
  fileStorageMgrRef,
  externalSystemMgrRef,
  schemaAPIMgrRef,
  coreState,
  buildWorkspaceResourceQueryParams,
  setConfigurationActionState,
  setConnectionResultDialog,
  setRecentLogsRefreshKey,
  loadCatalog,
  saveNewLinkedDestination
}) => {
  const [destinationScreenState, setDestinationScreenState] = useState(() => ({
    ...emptyDestinationState,
    linkedRecordId: isDestinationConfigurationScreen && selectedDestinationId ? selectedDestinationId : "",
    defaults: isDestinationConfigurationScreen ? selectedDestinationDefaults : null,
    loading: Boolean(isDestinationConfigurationScreen && selectedDestinationId)
  }));
  const [destinationRefreshKey, setDestinationRefreshKey] = useState(0);
  const [editDestinationPanelOpen, setEditDestinationPanelOpen] = useState(false);
  const [editDestinationPanelData, setEditDestinationPanelData] = useState(null);
  const [editDestinationPanelMetadata, setEditDestinationPanelMetadata] = useState(null);
  const [editDestinationPanelDefaults, setEditDestinationPanelDefaults] = useState(null);
  const [editDestinationPanelMode, setEditDestinationPanelMode] = useState("edit");
  const [editDestinationDeploymentPanelOpen, setEditDestinationDeploymentPanelOpen] = useState(false);
  const [editDestinationDeploymentPanelData, setEditDestinationDeploymentPanelData] = useState(null);
  const [editDestinationEntityDeploymentPanelOpen, setEditDestinationEntityDeploymentPanelOpen] = useState(false);
  const [editDestinationEntityDeploymentPanelData, setEditDestinationEntityDeploymentPanelData] = useState(null);
  const [editEndpointEntityEndpointPanelOpen, setEditEndpointEntityEndpointPanelOpen] = useState(false);
  const [editEndpointEntityEndpointPanelData, setEditEndpointEntityEndpointPanelData] = useState(null);
  const [endpointEntityObjectSelectOptionsByDeployment, setEndpointEntityObjectSelectOptionsByDeployment] = useState({});
  const [deleteDestinationDeploymentRequest, setDeleteDestinationDeploymentRequest] = useState(null);
  const [deleteDestinationEntityDeploymentRequest, setDeleteDestinationEntityDeploymentRequest] = useState(null);
  const [deleteEndpointEntityEndpointRequest, setDeleteEndpointEntityEndpointRequest] = useState(null);
  const [destinationEntityPickerOpen, setDestinationEntityPickerOpen] = useState(false);
  const [destinationEntityPickerSelection, setDestinationEntityPickerSelection] = useState([]);
  const [destinationEntityPickerDeploymentId, setDestinationEntityPickerDeploymentId] = useState("");
  const [destinationDeploymentManualRunDialog, setDestinationDeploymentManualRunDialog] = useState({
    open: false,
    deployment: null,
    values: {},
    error: "",
    feedback: createDestinationManualRunFeedbackState()
  });
  const destinationDeploymentManualRunFeedbackRef = useRef(null);
  const [warehouseDeploymentDeployDialog, setWarehouseDeploymentDeployDialog] = useState({
    open: false,
    deployment: null,
    values: { eoids: [], includeViews: false, as: false },
    error: ""
  });
  const [warehouseDeploymentDropDialog, setWarehouseDeploymentDropDialog] = useState({
    open: false,
    deployment: null,
    error: ""
  });
  const [warehouseDeploymentCloneDialog, setWarehouseDeploymentCloneDialog] = useState({
    open: false,
    deployment: null,
    values: { dbn: "", whn: "", svid: "" },
    error: ""
  });
  const [endpointDeploymentLifecycleConfirmDialog, setEndpointDeploymentLifecycleConfirmDialog] = useState({
    open: false,
    action: "",
    deployment: null
  });
  const refreshDestinationWorkspace = useCallback(() => {
    setDestinationRefreshKey((current) => current + 1);
  }, []);

  const routeReadyDestinationScreenState = useMemo(() => {
    const shouldResolveDestination = Boolean(isDestinationConfigurationScreen && selectedDestinationId);
    if (!shouldResolveDestination) return destinationScreenState;

    const routeLinkedId = normalizeCompare(selectedDestinationId);
    const stateLinkedId = normalizeCompare(getString(destinationScreenState, "linkedRecordId", "LinkedRecordId"));
    const hasLoadedRouteState = stateLinkedId === routeLinkedId
      && (destinationScreenState.loading || destinationScreenState.error || getId(destinationScreenState.destination));

    if (hasLoadedRouteState) return destinationScreenState;

    return {
      ...destinationScreenState,
      linkedRecordId: selectedDestinationId,
      destination: null,
      metadata: null,
      deploymentMetadata: null,
      entityDeploymentMetadata: null,
      schema: null,
      entityObjects: [],
      sourceObjects: [],
      warehouseDestinations: [],
      defaults: selectedDestinationDefaults,
      loading: true,
      error: ""
    };
  }, [destinationScreenState, isDestinationConfigurationScreen, selectedDestinationDefaults, selectedDestinationId]);

  const getDestinationManager = useCallback((defaults) => {
    const managerKey = defaults?.managerKey;
    if (managerKey === "warehouse") return warehouseMgrRef.current;
    if (managerKey === "fileStorage") return fileStorageMgrRef.current;
    if (managerKey === "externalSystem") return externalSystemMgrRef.current;
    if (managerKey === "schemaAPI") return schemaAPIMgrRef.current;
    return null;
  }, [externalSystemMgrRef, fileStorageMgrRef, schemaAPIMgrRef, warehouseMgrRef]);

  const destinationDeploymentManualRunStream = useMemo(() => ({
    manager: getDestinationManager(routeReadyDestinationScreenState.defaults),
    operationName: getDestinationManualRunOperationName(
      routeReadyDestinationScreenState.defaults,
      destinationDeploymentManualRunDialog.values || {}
    ),
    refId: getDeploymentId(destinationDeploymentManualRunDialog.deployment)
  }), [
    destinationDeploymentManualRunDialog.deployment,
    destinationDeploymentManualRunDialog.values,
    getDestinationManager,
    routeReadyDestinationScreenState.defaults
  ]);

  const clearGlobalWarnings = useCallback(() => {
    if (typeof coreState?.remove === "function") {
      coreState.remove("warning");
    }
  }, [coreState]);

  const showDestinationSaveError = useCallback((title, error, fallbackMessage) => {
    if (
      hasBackendValidationErrors(coreState?.global?.get?.("backendValidationErrors"))
      || hasBackendValidationErrors(getBackendValidationErrorsFromResponse(error?.response))
    ) {
      return;
    }

    const message = error?.message || fallbackMessage;
    const details = [
      ...(error?.details || []),
      ...getOperationResultDebugDetails(error?.response)
    ].filter(Boolean).filter((detail, index, allDetails) =>
      allDetails.findIndex((candidate) => normalizeCompare(candidate) === normalizeCompare(detail)) === index
    );
    setConnectionResultDialog?.({
      title,
      status: "error",
      message,
      details
    });
  }, [coreState, setConnectionResultDialog]);

  const saveDestinationItem = useCallback(async (activeDestinationMgr, payload) => {
    const itemService = activeDestinationMgr?._itemService;
    if (
      itemService
      && typeof itemService._createAxiosInstance === "function"
      && typeof itemService._checkCoreError === "function"
      && itemService._url
    ) {
      const axiosInstance = itemService._createAxiosInstance();
      let response;
      try {
        response = await axiosInstance.put(itemService._url, payload);
      } catch (axiosError) {
        response = axiosError?.response || {
          status: "",
          statusText: axiosError?.message || "Request failed",
          data: [{
            Success: false,
            Message: {
              DisplayMessage: axiosError?.message || "Request failed.",
              Code: "JS_ERROR",
              Info: axiosError?.stack
            },
            MessageDetails: axiosError?.stack ? [axiosError.stack] : [],
            Items: []
          }]
        };
      }
      return attachRawDestinationSaveResponse(itemService._checkCoreError(response, true, true), response);
    }
    return activeDestinationMgr.saveItem(payload);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadLinkedDestination = async () => {
      setEndpointEntityObjectSelectOptionsByDeployment({});
      if (!isDestinationConfigurationScreen) {
        setDestinationScreenState(emptyDestinationState);
        return;
      }

      if (!selectedDestinationId) {
        setDestinationScreenState({
          ...emptyDestinationState,
          defaults: selectedDestinationDefaults,
          error: "No Destination is linked to this Resource Configuration."
        });
        return;
      }

      if (!profileContextReady) {
        setDestinationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedDestinationId,
          destination: null,
          metadata: null,
          deploymentMetadata: null,
          entityDeploymentMetadata: null,
          schema: null,
          entityObjects: [],
          sourceObjects: [],
          warehouseDestinations: [],
          defaults: selectedDestinationDefaults,
          loading: true,
          error: ""
        }));
        return;
      }

      const activeDestinationMgr = getDestinationManager(selectedDestinationDefaults);
      const activeSchemaMgr = schemaMgrRef.current;
      const activeEntityObjectMgr = entityObjectMgrRef.current;
      if (!selectedDestinationDefaults || !activeDestinationMgr?.GetMetadata || (!activeDestinationMgr?.getItem && !activeDestinationMgr?.Query)) {
        setDestinationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedDestinationId,
          destination: null,
          metadata: null,
          deploymentMetadata: null,
          entityDeploymentMetadata: null,
          schema: null,
          entityObjects: [],
          sourceObjects: [],
          warehouseDestinations: [],
          defaults: selectedDestinationDefaults,
          loading: false,
          error: "Destination service is not available."
        }));
        return;
      }

      setDestinationScreenState((previous) => ({
        ...previous,
        linkedRecordId: selectedDestinationId,
        destination: null,
        schema: null,
        entityObjects: [],
        sourceObjects: [],
        warehouseDestinations: [],
        defaults: selectedDestinationDefaults,
        loading: true,
        error: ""
      }));

      try {
        const destinationPromise = activeDestinationMgr.getItem
          ? activeDestinationMgr.getItem(selectedDestinationId)
          : activeDestinationMgr.Query({ model: "x => x.IsDeleted == false" });
        const [destinationResult, metadataResult] = await Promise.all([
          destinationPromise,
          activeDestinationMgr.GetMetadata({ forceReload: true })
        ]);

        const destinationItems = getOperationItems(destinationResult);
        const loadedDestination = getOperationItem(destinationResult)
          || destinationItems.find((item) => normalizeCompare(getId(item)) === normalizeCompare(selectedDestinationId))
          || null;
        const metadataItems = getOperationItems(metadataResult);
        const loadedMetadata = metadataItems.find((item) =>
          normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === normalizeCompare(selectedDestinationDefaults.itemName)
          || normalizeCompare(getString(item, "DisplayName", "displayName")) === normalizeCompare(selectedDestinationDefaults.label)
        )
          || firstItem(metadataResult?.Items)
          || firstItem(metadataResult)
          || firstItem(activeDestinationMgr.uiMetadata)
          || null;
        const loadedDeploymentMetadata = resolveDestinationDeploymentMetadata(metadataItems, selectedDestinationDefaults);
        const loadedEntityDeploymentMetadata = resolveEntityDeploymentMetadata(metadataItems);

        let loadedSchema = null;
        let loadedEntityObjects = [];
        let loadedSourceObjects = [];
        let loadedWarehouseDestinations = [];
        const schemaId = getDestinationSchemaId(loadedDestination);
        const schemaVersionId = getDestinationActiveSchemaVersionId(loadedDestination);
        const endpointSchemaVersionIds = selectedDestinationDefaults?.isEndpoint
          ? getDestinationDeployments(loadedDestination, selectedDestinationDefaults)
            .flatMap(getEndpointDeploymentSchemaScopes)
            .map((scope) => getString(scope, "SchemaVersionId", "schemaVersionId"))
            .filter(Boolean)
          : [];
        const entityObjectSchemaVersionIds = Array.from(new Set(
          [schemaVersionId, ...endpointSchemaVersionIds].map(normalizeCompare).filter(Boolean)
        ));
        const endpointWarehouseIds = selectedDestinationDefaults?.isEndpoint
          ? getEndpointWarehouseIds(loadedDestination, selectedDestinationDefaults)
          : [];
        if (endpointWarehouseIds.length > 0 && warehouseMgrRef.current) {
          const activeWarehouseMgr = warehouseMgrRef.current;
          const normalizedEndpointWarehouseIds = new Set(endpointWarehouseIds.map(normalizeCompare));
          if (activeWarehouseMgr.getItem) {
            const warehouseResults = await Promise.all(endpointWarehouseIds.map(async (warehouseId) => {
              try {
                return await activeWarehouseMgr.getItem(warehouseId);
              } catch {
                return null;
              }
            }));
            loadedWarehouseDestinations = getUniqueRowsByKey(
              warehouseResults
                .flatMap((warehouseResult) => [
                  getOperationItem(warehouseResult),
                  ...getOperationItems(warehouseResult)
                ])
                .filter((item) => normalizedEndpointWarehouseIds.has(normalizeCompare(getId(item)))),
              (warehouse, index) => getId(warehouse) || `warehouse-${index}`
            );
          }
          if (loadedWarehouseDestinations.length === 0 && activeWarehouseMgr.Query) {
            const warehouseResult = await activeWarehouseMgr.Query({ model: "x => x.IsDeleted == false" });
            loadedWarehouseDestinations = getUniqueRowsByKey(
              getOperationItems(warehouseResult).filter((item) => normalizedEndpointWarehouseIds.has(normalizeCompare(getId(item)))),
              (warehouse, index) => getId(warehouse) || `warehouse-${index}`
            );
          }
        }
        if (loadedDestination && schemaId && activeSchemaMgr?.Query) {
          const schemaResult = await activeSchemaMgr.Query({ model: "x => x.IsDeleted == false" });
          loadedSchema = getOperationItems(schemaResult).find((item) =>
            normalizeCompare(getId(item)) === normalizeCompare(schemaId)
            || normalizeCompare(getString(item, "SchemaId", "schemaId", "UniqueId", "uniqueId")) === normalizeCompare(schemaId)
          ) || null;
        }
        if (loadedDestination && entityObjectSchemaVersionIds.length > 0 && activeEntityObjectMgr?.Query) {
          const entityObjectResults = await Promise.all(entityObjectSchemaVersionIds.map((activeSchemaVersionId) =>
            activeEntityObjectMgr.Query({
              model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
              params: { schemaVersionId: activeSchemaVersionId }
            })
          ));
          const allowedSchemaVersionIds = new Set(entityObjectSchemaVersionIds);
          loadedEntityObjects = getUniqueRowsByKey(
            entityObjectResults
              .flatMap(getOperationItems)
              .filter((item) =>
                !isRecordDeleted(item)
                && allowedSchemaVersionIds.has(normalizeCompare(getString(item, "SchemaVersionId", "schemaVersionId")))
              ),
            (entityObject, index) => getId(entityObject) || getString(entityObject, "EntityObjectId", "entityObjectId", "ObjectName", "objectName") || `entity-object-${index}`
          ).sort((left, right) =>
            getDisplayName(left, getString(left, "ObjectName", "objectName"))
              .localeCompare(getDisplayName(right, getString(right, "ObjectName", "objectName")))
          );
          const sourceObjectIds = new Set(loadedEntityObjects.map(getEntityObjectSourceObjectId).map(normalizeCompare).filter(Boolean));
          if (sourceObjectIds.size > 0 && sourceObjectMgrRef.current?.Query) {
            const sourceObjectResult = await sourceObjectMgrRef.current.Query({ model: "x => x.IsDeleted == false" });
            if (!(getOperationResult(sourceObjectResult)?.Success === false || sourceObjectResult?.Success === false)) {
              loadedSourceObjects = getSelectedSourceObjectsForEntityObjects(loadedEntityObjects, getOperationItems(sourceObjectResult))
                .sort((left, right) =>
                  getDisplayName(left, getString(left, "SystemName", "systemName", "ObjectName", "objectName"))
                    .localeCompare(getDisplayName(right, getString(right, "SystemName", "systemName", "ObjectName", "objectName")))
                );
            }
          }
        }

        if (!mounted) return;
        setDestinationScreenState({
          linkedRecordId: selectedDestinationId,
          destination: loadedDestination,
          metadata: loadedMetadata,
          deploymentMetadata: loadedDeploymentMetadata,
          entityDeploymentMetadata: loadedEntityDeploymentMetadata,
          schema: loadedSchema,
          entityObjects: loadedEntityObjects,
          sourceObjects: loadedSourceObjects,
          warehouseDestinations: loadedWarehouseDestinations,
          defaults: selectedDestinationDefaults,
          loading: false,
          error: loadedDestination ? "" : "The linked Destination could not be loaded."
        });
      } catch (loadDestinationError) {
        if (!mounted) return;
        setDestinationScreenState({
          linkedRecordId: selectedDestinationId,
          destination: null,
          metadata: null,
          deploymentMetadata: null,
          entityDeploymentMetadata: null,
          schema: null,
          entityObjects: [],
          sourceObjects: [],
          warehouseDestinations: [],
          defaults: selectedDestinationDefaults,
          loading: false,
          error: loadDestinationError?.message || "The linked Destination could not be loaded."
        });
      }
    };

    loadLinkedDestination();

    return () => {
      mounted = false;
    };
  }, [
    destinationRefreshKey,
    entityObjectMgrRef,
    getDestinationManager,
    isDestinationConfigurationScreen,
    profileContextReady,
    schemaMgrRef,
    selectedDestinationDefaults,
    selectedDestinationId,
    sourceObjectMgrRef,
    warehouseMgrRef
  ]);

  const saveDestinationPayload = useCallback(async (payload, busyAction, fallbackErrorMessage) => {
    const activeDestinationMgr = getDestinationManager(destinationScreenState.defaults);
    if (!activeDestinationMgr?.saveItem) {
      throw new Error("Destination service is not available.");
    }

    setConfigurationActionState({ busyAction, error: "" });
    let response;
    const payloadForSave = normalizeDestinationSavePayload({
      ...payload,
      IsNew: false
    }, destinationScreenState.defaults);
    try {
      response = await saveDestinationItem(activeDestinationMgr, payloadForSave);
    } catch (saveError) {
      clearGlobalWarnings();
      throw saveError;
    }
    const rawResponse = getRawDestinationSaveResponse(response);
    const operationResponse = getDestinationSaveOperationResponse(response);
    const result = getOperationResult(operationResponse);
    if (result?.Success === false || result?.success === false) {
      clearGlobalWarnings();
      const error = new Error(getOperationResultMessage(operationResponse, fallbackErrorMessage));
      error.response = rawResponse;
      error.details = [
        ...getOperationResultDetails(operationResponse),
        ...getOperationResultDebugDetails(rawResponse)
      ];
      throw error;
    }

    const savedDestination = getOperationItem(operationResponse)
      || firstItem(operationResponse?.Items)
      || firstItem(response?.Items)
      || payloadForSave;
    setDestinationScreenState((previous) => ({
      ...previous,
      destination: savedDestination,
      error: ""
    }));
    setConfigurationActionState({ busyAction: "", error: "" });
    setDestinationRefreshKey((current) => current + 1);
    loadCatalog();
    return response;
  }, [clearGlobalWarnings, destinationScreenState.defaults, getDestinationManager, loadCatalog, saveDestinationItem, setConfigurationActionState]);

  const openEditDestinationPanel = useCallback(() => {
    const destination = destinationScreenState.destination;
    if (!getId(destination)) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Destination is available to edit."
      });
      return;
    }
    if (!destinationScreenState.metadata) {
      setConfigurationActionState({
        busyAction: "",
        error: "Destination metadata is not available yet."
      });
      return;
    }

    setEditDestinationPanelData({
      ...destination,
      IsNew: false
    });
    setEditDestinationPanelMetadata(destinationScreenState.metadata);
    setEditDestinationPanelDefaults(destinationScreenState.defaults);
    setEditDestinationPanelMode("edit");
    setEditDestinationPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, destinationScreenState.metadata, setConfigurationActionState]);

  const openAddDestinationPanel = useCallback(async (defaults, { templateDestinationId = "" } = {}) => {
    const activeDestinationMgr = getDestinationManager(defaults);
    if (!activeDestinationMgr?.GetMetadata) {
      setConfigurationActionState({ busyAction: "", error: "Endpoint service is not available." });
      return;
    }

    setConfigurationActionState({ busyAction: "destination-add-load", error: "" });
    try {
      const [metadataResult, templateResult] = await Promise.all([
        activeDestinationMgr.GetMetadata({ forceReload: true }),
        templateDestinationId && activeDestinationMgr.getItem
          ? activeDestinationMgr.getItem(templateDestinationId)
          : Promise.resolve(null)
      ]);
      const metadataItems = getOperationItems(metadataResult);
      const metadata = metadataItems.find((item) =>
        normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === normalizeCompare(defaults?.itemName)
      ) || firstItem(metadataResult?.Items) || firstItem(metadataResult) || firstItem(activeDestinationMgr.uiMetadata);
      if (!metadata) throw new Error("Endpoint metadata is not available.");

      const templateDestination = getOperationItem(templateResult);
      const addEndpointDraft = buildDestinationDraft({
        wizard: null,
        values: templateDestination ? {
          SchemaId: getDestinationSchemaId(templateDestination),
          ActiveSchemaVersionId: getDestinationActiveSchemaVersionId(templateDestination)
        } : {},
        defaults,
        coreContext
      });
      setEditDestinationPanelData({
        ...addEndpointDraft,
        Name: "",
        SystemName: "",
        APIName: ""
      });
      setEditDestinationPanelMetadata({
        ...collapseAddEndpointMetadataTabs(buildDestinationFormMetadata(metadata, defaults, { lockSchema: false })),
        ...(defaults?.label ? { DisplayName: defaults.label } : {})
      });
      setEditDestinationPanelDefaults(defaults);
      setEditDestinationPanelMode("add");
      setEditDestinationPanelOpen(true);
      setConfigurationActionState({ busyAction: "", error: "" });
    } catch (error) {
      setConfigurationActionState({ busyAction: "", error: error?.message || "Endpoint metadata could not be loaded." });
    }
  }, [coreContext, getDestinationManager, setConfigurationActionState]);

  const closeEditDestinationPanel = useCallback(() => {
    setEditDestinationPanelOpen(false);
    setEditDestinationPanelData(null);
    setEditDestinationPanelMetadata(null);
    setEditDestinationPanelDefaults(null);
    setEditDestinationPanelMode("edit");
  }, []);

  const saveDestinationDetails = useCallback(async (updatedDestination) => {
    if (editDestinationPanelMode === "add") {
      if (typeof saveNewLinkedDestination !== "function") {
        setConfigurationActionState({ busyAction: "", error: "Endpoint link service is not available." });
        return undefined;
      }

      try {
        const updatedEndpoint = {
          ...editDestinationPanelData,
          ...(updatedDestination || {})
        };
        if (!getString(updatedEndpoint, "Name", "name").trim()) {
          throw new Error("Endpoint name is required.");
        }
        const payload = ensureDestinationDefaults(updatedEndpoint, {
          wizard: null,
          defaults: editDestinationPanelDefaults,
          coreContext
        });
        setConfigurationActionState({ busyAction: "destination-add", error: "" });
        const response = await saveNewLinkedDestination(payload, editDestinationPanelDefaults);
        closeEditDestinationPanel();
        setConfigurationActionState({ busyAction: "", error: "" });
        return response;
      } catch (saveDestinationError) {
        setConfigurationActionState({ busyAction: "", error: saveDestinationError?.message || "Endpoint could not be saved." });
        showDestinationSaveError("Save Endpoint", saveDestinationError, "Endpoint could not be saved.");
        return undefined;
      }
    }

    const destinationId = getId(updatedDestination) || getId(editDestinationPanelData);
    if (!destinationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Destination could not be saved because it is missing an id."
      });
      return undefined;
    }

    try {
      const currentDeployments = getDestinationDeployments(editDestinationPanelData, destinationScreenState.defaults);
      const currentEntityDeployments = getDestinationEntityDeployments(editDestinationPanelData);
      const response = await saveDestinationPayload(
        {
          ...editDestinationPanelData,
          ...(updatedDestination || {}),
          Deployments: currentDeployments,
          EntityObjectDeployments: currentEntityDeployments
        },
        `destination-edit:${destinationId}`,
        "Destination could not be saved."
      );
      closeEditDestinationPanel();
      return response;
    } catch (saveDestinationError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveDestinationError?.message || "Destination could not be saved."
      });
      showDestinationSaveError("Save Destination", saveDestinationError, "Destination could not be saved.");
      return undefined;
    }
  }, [
    closeEditDestinationPanel,
    coreContext,
    destinationScreenState.defaults,
    editDestinationPanelData,
    editDestinationPanelDefaults,
    editDestinationPanelMode,
    saveDestinationPayload,
    saveNewLinkedDestination,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const setDestinationActive = useCallback(async (isActive) => {
    const destination = destinationScreenState.destination;
    const destinationId = getId(destination);
    if (!destinationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Destination is available to update."
      });
      return;
    }

    try {
      await saveDestinationPayload(
        {
          ...destination,
          IsActive: isActive,
          isActive
        },
        `${isActive ? "destination-enable" : "destination-disable"}:${destinationId}`,
        "Destination status could not be updated."
      );
    } catch (statusError) {
      setConfigurationActionState({
        busyAction: "",
        error: statusError?.message || "Destination status could not be updated."
      });
      showDestinationSaveError("Update Destination Status", statusError, "Destination status could not be updated.");
    }
  }, [destinationScreenState.destination, saveDestinationPayload, setConfigurationActionState, showDestinationSaveError]);

  const runDestinationConnectionCheck = useCallback(async (deployment = null) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const destinationId = getId(destination);
    if (!destinationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Destination connection check is not available."
      });
      return;
    }

    try {
      const checkDeployment = deployment || getDefaultDestinationDeployment(destination, defaults);
      const checkTarget = checkDeployment || destination;
      const checkName = checkDeployment
        ? getDeploymentDisplayName(checkDeployment, destination, getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination)))
        : getDisplayName(destination, defaults?.label || "Destination");

      if (defaults?.isEndpoint) {
        if (!checkDeployment) throw new Error("API endpoint deployment was not found.");
        const clientShortName = await resolveClientShortNameAsync({
          endpoint: destination,
          deployment: checkDeployment,
          coreContext,
          clientMgr: clientMgrRef?.current
        });
        const statusUrl = buildStatusUrl(destination, checkDeployment, clientShortName);
        if (!statusUrl) throw new Error("Status URL cannot be built because the endpoint client route key or deployment key is missing.");

        setConfigurationActionState({ busyAction: `destination-check:${getDeploymentId(checkTarget) || destinationId}`, error: "" });
        const response = await fetch(statusUrl, { method: "GET" });
        const { payload, formatted } = await readStatusResponse(response);
        const details = buildEndpointStatusDetails({ statusUrl, response, payload, formatted });
        setConfigurationActionState({ busyAction: "", error: "" });
        setConnectionResultDialog({
          title: "Endpoint Status Check",
          status: response.ok ? "success" : "error",
          message: response.ok
            ? `${checkName}: Status endpoint returned ${response.status} ${response.statusText || "OK"}.`.trim()
            : `${checkName}: Status endpoint returned HTTP ${response.status}.`,
          details,
          endpointStatus: {
            statusUrl,
            httpStatus: response.status,
            httpStatusText: response.statusText || "",
            payload,
            formatted
          }
        });
        return;
      }

      const operationName = defaults?.checkOperationName || defaults?.validateOperationName;
      if (!operationName) {
        setConfigurationActionState({
          busyAction: "",
          error: "Destination connection check is not available."
        });
        return;
      }
      const activeDestinationMgr = getDestinationManager(defaults);
      if (!activeDestinationMgr?.RunOperation) {
        throw new Error("Destination operation service is not available.");
      }
      const checkMetadata = buildDestinationConnectionCheckMetadata({
        destination,
        deployment: checkDeployment ? withDeploymentConnectionCheckId(checkDeployment) : null,
        defaults: toDestinationSetupDefaults(defaults)
      });
      setConfigurationActionState({ busyAction: `destination-check:${getId(checkTarget) || getDeploymentId(checkTarget) || destinationId}`, error: "" });
      const response = await activeDestinationMgr.RunOperation({
        operationName,
        item: destination,
        queryParams: buildWorkspaceResourceQueryParams(checkMetadata, destination),
        isItem: true,
        isWarning: false,
        supressAllErrors: true
      });
      const result = getOperationResult(response);
      const success = !(result?.Success === false || result?.success === false);
      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog({
        title: "Destination Connection Check",
        status: success ? "success" : "error",
        message: getOperationResultMessage(response, success ? `${checkName}: Connection successful.` : `${checkName}: Connection check failed.`),
        details: getOperationResultDetails(response)
      });
      setRecentLogsRefreshKey((current) => current + 1);
    } catch (checkError) {
      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog({
        title: "Destination Connection Check",
        status: "error",
        message: checkError?.message || "Destination connection check failed.",
        details: []
      });
    }
  }, [
    buildWorkspaceResourceQueryParams,
    clientMgrRef,
    coreContext,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    getDestinationManager,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey
  ]);

  const runEndpointDeploymentOperation = useCallback(async ({ deployment, operationName, busyAction, fallbackMessage }) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const activeDestinationMgr = getDestinationManager(defaults);
    if (!defaults?.isEndpoint || !getId(destination) || !deployment || !getDeploymentId(deployment) || !operationName) {
      throw new Error(fallbackMessage || "Endpoint deployment operation is not available.");
    }
    if (!activeDestinationMgr?.RunOperation) {
      throw new Error("Endpoint operation service is not available.");
    }

    const metadata = buildDestinationConnectionCheckMetadata({
      destination,
      deployment: withDeploymentConnectionCheckId(deployment),
      defaults: toDestinationSetupDefaults(defaults)
    });
    setConfigurationActionState({ busyAction, error: "" });
    return activeDestinationMgr.RunOperation({
      operationName,
      item: destination,
      queryParams: buildWorkspaceResourceQueryParams(metadata, destination),
      isItem: true,
      isWarning: false,
      supressAllErrors: true
    });
  }, [
    buildWorkspaceResourceQueryParams,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    getDestinationManager,
    setConfigurationActionState
  ]);

  const loadEndpointEntityObjectSelectOptions = useCallback(async (deployment) => {
    const deploymentId = getDeploymentId(deployment);
    const operationName = destinationScreenState.defaults?.entityObjectSelectDataOperationName
      || "SelectDataAPIEndpointDeploymentEntityObject";
    const response = await runEndpointDeploymentOperation({
      deployment,
      operationName,
      busyAction: `endpoint-entity-options:${deploymentId}`,
      fallbackMessage: "Endpoint Entity Object options are not available."
    });
    if (!isOperationSuccessful(response)) {
      throw new Error(getOperationResultMessage(response) || "Endpoint Entity Object options could not be loaded.");
    }

    const selectDataItem = getOperationItem(response);
    const options = getArray(selectDataItem, "TextValues", "textValues");
    setEndpointEntityObjectSelectOptionsByDeployment((previous) => ({
      ...previous,
      [normalizeCompare(deploymentId)]: options
    }));
    return options;
  }, [
    destinationScreenState.defaults?.entityObjectSelectDataOperationName,
    runEndpointDeploymentOperation
  ]);

  const undeployEndpointDeployment = useCallback(async (deployment, options = {}) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    const deploymentName = getDeploymentDisplayName(
      deployment,
      destination,
      getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination))
    );
    const operationName = defaults?.dropOperationName;
    try {
      const response = await runEndpointDeploymentOperation({
        deployment,
        operationName,
        busyAction: `endpoint-undeploy:${deploymentId}`,
        fallbackMessage: "Endpoint undeploy is not available."
      });
      const success = isOperationSuccessful(response);
      if (!success) {
        throw Object.assign(new Error(getOperationResultMessage(response, `${deploymentName}: Undeploy failed.`)), {
          details: getLifecycleOperationDetails(response)
        });
      }
      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog?.({
        title: "Endpoint Undeploy",
        status: "success",
        message: getOperationResultMessage(response, `${deploymentName}: API deployment is set to Offline.`),
        details: getLifecycleOperationDetails(response)
      });
      setDestinationRefreshKey((current) => current + 1);
      setRecentLogsRefreshKey((current) => current + 1);
      loadCatalog();
      return response;
    } catch (undeployError) {
      setConfigurationActionState({ busyAction: "", error: "" });
      if (!options.silent) {
        setConnectionResultDialog?.({
          title: "Endpoint Undeploy",
          status: "error",
          message: undeployError?.message || "Endpoint undeploy failed.",
          details: undeployError?.details || []
        });
      }
      if (options.silent) throw undeployError;
      return null;
    }
  }, [
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    loadCatalog,
    runEndpointDeploymentOperation,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey
  ]);

  const deployEndpointDeployment = useCallback(async (deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    const deploymentName = getDeploymentDisplayName(
      deployment,
      destination,
      getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination))
    );
    const operationName = defaults?.deployOperationName;
    const activeDestinationMgr = getDestinationManager(defaults);
    try {
      const response = await runEndpointDeploymentOperation({
        deployment,
        operationName,
        busyAction: `endpoint-deploy:${deploymentId}`,
        fallbackMessage: "Endpoint deploy is not available."
      });
      const operationSuccess = isOperationSuccessful(response);
      if (!operationSuccess) {
        throw Object.assign(new Error(getOperationResultMessage(response, `${deploymentName}: Deploy failed.`)), {
          details: getLifecycleOperationDetails(response)
        });
      }

      const operationDetails = getLifecycleOperationDetails(response);
      if (isEndpointDeploymentMcpOnly(deployment)) {
        setConfigurationActionState({ busyAction: "", error: "" });
        setConnectionResultDialog?.({
          title: "Endpoint Deploy",
          status: "success",
          message: getOperationResultMessage(response, `${deploymentName}: MCP endpoint deployed.`),
          details: [
            ...operationDetails,
            "Hosted API secure status skipped because this deployment is MCP-only."
          ]
        });
        setDestinationRefreshKey((current) => current + 1);
        setRecentLogsRefreshKey((current) => current + 1);
        loadCatalog();
        return response;
      }

      if (isEndpointDeploymentBasicOnly(deployment)) {
        setConfigurationActionState({ busyAction: "", error: "" });
        setConnectionResultDialog?.({
          title: "Endpoint Deploy",
          status: "success",
          message: getOperationResultMessage(response, `${deploymentName}: Endpoint deployed. Basic-only deployments use the saved Basic credential test, so bearer secure status was skipped.`),
          details: [
            ...operationDetails,
            "Bearer secure status skipped because this deployment is Basic-only."
          ]
        });
        setDestinationRefreshKey((current) => current + 1);
        setRecentLogsRefreshKey((current) => current + 1);
        loadCatalog();
        return response;
      }

      const clientShortName = await resolveClientShortNameAsync({
        endpoint: destination,
        deployment,
        coreContext,
        clientMgr: clientMgrRef?.current
      });
      const statusUrl = buildStatusUrl(destination, deployment, clientShortName);
      const secureStatusUrl = buildEndpointSecureStatusUrl(statusUrl);
      if (!secureStatusUrl) throw new Error("Secure status URL cannot be built because the endpoint client route key or deployment key is missing.");

      const secureStatusResult = await readEndpointJsonRequest(activeDestinationMgr, secureStatusUrl);
      if (!secureStatusResult.ok) {
        await undeployEndpointDeployment(deployment, { silent: true }).catch(() => null);
        const secureStatusMessage = `${deploymentName}: Deploy rolled back because secure status returned ${secureStatusResult.status || "an error"} ${secureStatusResult.statusText || ""}.`.trim();
        setConfigurationActionState({ busyAction: "", error: "" });
        setConnectionResultDialog?.({
          title: "Endpoint Deploy",
          status: "error",
          message: secureStatusMessage,
          details: [
            ...operationDetails,
            `Secure status URL: ${secureStatusUrl}`,
            `HTTP status: ${secureStatusResult.status || "Not set"} ${secureStatusResult.statusText || ""}`.trim(),
            secureStatusResult.formatted ? `Secure status JSON:\n${secureStatusResult.formatted}` : ""
          ].filter(Boolean),
          endpointStatus: {
            statusUrl: secureStatusUrl,
            httpStatus: secureStatusResult.status,
            httpStatusText: secureStatusResult.statusText || "",
            payload: secureStatusResult.payload,
            formatted: secureStatusResult.formatted
          }
        });
        return response;
      }

      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog?.({
        title: "Endpoint Deploy",
        status: "success",
        message: getOperationResultMessage(response, `${deploymentName}: Endpoint deployed and secure status passed.`),
        details: operationDetails,
        endpointStatus: {
          statusUrl: secureStatusUrl,
          httpStatus: secureStatusResult.status,
          httpStatusText: secureStatusResult.statusText || "",
          payload: secureStatusResult.payload,
          formatted: secureStatusResult.formatted
        }
      });
      setDestinationRefreshKey((current) => current + 1);
      setRecentLogsRefreshKey((current) => current + 1);
      loadCatalog();
      return response;
    } catch (deployError) {
      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog?.({
        title: "Endpoint Deploy",
        status: "error",
        message: deployError?.message || "Endpoint deploy failed.",
        details: deployError?.details || []
      });
      return null;
    }
  }, [
    clientMgrRef,
    coreContext,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    getDestinationManager,
    loadCatalog,
    runEndpointDeploymentOperation,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey,
    undeployEndpointDeployment
  ]);

  const requestDeployEndpointDeployment = useCallback((deployment) => {
    if (!deployment) return;
    setEndpointDeploymentLifecycleConfirmDialog({
      open: true,
      action: "deploy",
      deployment
    });
  }, []);

  const requestUndeployEndpointDeployment = useCallback((deployment) => {
    if (!deployment) return;
    setEndpointDeploymentLifecycleConfirmDialog({
      open: true,
      action: "undeploy",
      deployment
    });
  }, []);

  const closeEndpointDeploymentLifecycleConfirmDialog = useCallback(() => {
    setEndpointDeploymentLifecycleConfirmDialog({
      open: false,
      action: "",
      deployment: null
    });
  }, []);

  const confirmEndpointDeploymentLifecycleAction = useCallback(async () => {
    const request = endpointDeploymentLifecycleConfirmDialog;
    if (!request?.open || !request.deployment || !request.action) return null;
    closeEndpointDeploymentLifecycleConfirmDialog();
    if (request.action === "undeploy") {
      return undeployEndpointDeployment(request.deployment);
    }
    return deployEndpointDeployment(request.deployment);
  }, [
    closeEndpointDeploymentLifecycleConfirmDialog,
    deployEndpointDeployment,
    endpointDeploymentLifecycleConfirmDialog,
    undeployEndpointDeployment
  ]);

  const openDestinationDeploymentManualRun = useCallback((deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    const operationName = getDestinationManualRunOperationName(defaults);
    if (!getId(destination) || !defaults || !deployment || !deploymentId || !operationName) {
      setConfigurationActionState({
        busyAction: "",
        error: "Manual run is not available for this deployment."
      });
      return;
    }
    if (!isRecordActive(destination) || !isRecordActive(deployment)) {
      setConfigurationActionState({
        busyAction: "",
        error: "Manual run is only available for active destinations and deployments."
      });
      return;
    }
    setDestinationDeploymentManualRunDialog({
      open: true,
      deployment,
      values: getDestinationManualRunInitialValues(defaults),
      error: "",
      feedback: createDestinationManualRunFeedbackState()
    });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, setConfigurationActionState]);

  const closeDestinationDeploymentManualRunDialog = useCallback(() => {
    destinationDeploymentManualRunFeedbackRef.current?.stop?.();
    setDestinationDeploymentManualRunDialog({
      open: false,
      deployment: null,
      values: {},
      error: "",
      feedback: createDestinationManualRunFeedbackState()
    });
  }, []);

  const setDestinationDeploymentManualRunValues = useCallback((values) => {
    setDestinationDeploymentManualRunDialog((previous) => ({
      ...previous,
      values: values || {},
      error: ""
    }));
  }, []);

  const runDestinationDeploymentManualRun = useCallback(async () => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deployment = destinationDeploymentManualRunDialog.deployment;
    const values = destinationDeploymentManualRunDialog.values || {};
    const deploymentId = getDeploymentId(deployment);
    const operationName = getDestinationManualRunOperationName(defaults, values);
    const deploymentName = getDeploymentDisplayName(
      deployment,
      destination,
      getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination))
    );

    if (!getId(destination) || !defaults || !deployment || !deploymentId || !operationName) {
      setDestinationDeploymentManualRunDialog((previous) => ({
        ...previous,
        error: "Manual run is not available for this deployment."
      }));
      return;
    }

    try {
      const activeDestinationMgr = getDestinationManager(defaults);
      if (!activeDestinationMgr?.RunOperation) {
        throw new Error("Destination operation service is not available.");
      }

      const selectedMode = getDestinationManualRunSelectedMode(defaults, values, true);
      const validationMessage = validateDestinationManualRunValues(defaults, values, selectedMode);
      if (validationMessage) {
        setDestinationDeploymentManualRunDialog((previous) => ({
          ...previous,
          error: validationMessage
        }));
        return;
      }

      const metadata = buildDestinationManualRunMetadata({ defaults, deployment, values });
      const processorType = normalizeDestinationManualRunProcessorType(values?.processorType);
      const isRealTimeProcessor = String(processorType) === String(DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.realTime);
      let streamStarted = false;
      let streamError = "";

      setConfigurationActionState({ busyAction: `destination-deployment-manual-run:${deploymentId}`, error: "" });
      if (isRealTimeProcessor) {
        flushSync(() => {
          setDestinationDeploymentManualRunDialog((previous) => ({
            ...previous,
            error: "",
            feedback: {
              ...createDestinationManualRunFeedbackState(),
              active: true
            }
          }));
        });
        try {
          destinationDeploymentManualRunFeedbackRef.current?.clear?.();
          streamStarted = await destinationDeploymentManualRunFeedbackRef.current?.start?.();
          if (!streamStarted) {
            streamError = "Live feedback stream could not be started. The manual run will still return a final result.";
          }
        } catch (feedbackError) {
          streamError = feedbackError?.message || "Live feedback stream could not be started. The manual run will still return a final result.";
        }
        if (streamError) {
          setDestinationDeploymentManualRunDialog((previous) => ({
            ...previous,
            feedback: {
              ...(previous.feedback || createDestinationManualRunFeedbackState()),
              active: true,
              streamStarted: false,
              streamError
            }
          }));
        } else {
          setDestinationDeploymentManualRunDialog((previous) => ({
            ...previous,
            feedback: {
              ...(previous.feedback || createDestinationManualRunFeedbackState()),
              active: true,
              streamStarted: true,
              streamError: ""
            }
          }));
        }
      }

      const response = await activeDestinationMgr.RunOperation({
        operationName,
        item: destination,
        queryParams: buildWorkspaceResourceQueryParams(metadata, destination),
        isItem: true,
        isWarning: true,
        processorType: Number(processorType),
        supressAllErrors: true
      });
      const result = getOperationResult(response);
      const success = !(result?.Success === false || result?.success === false);
      if (!success) {
        clearGlobalWarnings();
      }
      setConfigurationActionState({ busyAction: "", error: "" });
      if (isRealTimeProcessor) {
        setDestinationDeploymentManualRunDialog((previous) => ({
          ...previous,
          error: "",
          feedback: {
            ...(previous.feedback || createDestinationManualRunFeedbackState()),
            active: true,
            complete: true,
            success,
            streamStarted,
            streamError,
            message: getOperationResultMessage(response, success ? `${deploymentName}: Manual run completed.` : `${deploymentName}: Manual run failed.`),
            details: getOperationResultDetails(response)
          }
        }));
        setRecentLogsRefreshKey((current) => current + 1);
        return;
      }
      closeDestinationDeploymentManualRunDialog();
      setConnectionResultDialog({
        title: "Manual Run",
        status: success ? "success" : "error",
        message: getOperationResultMessage(response, success ? `${deploymentName}: Manual run started.` : `${deploymentName}: Manual run failed.`),
        details: getOperationResultDetails(response)
      });
      setRecentLogsRefreshKey((current) => current + 1);
    } catch (runError) {
      const message = runError?.message || "Manual run could not be started.";
      clearGlobalWarnings();
      setConfigurationActionState({ busyAction: "", error: "" });
      const processorType = normalizeDestinationManualRunProcessorType(values?.processorType);
      const isRealTimeProcessor = String(processorType) === String(DESTINATION_MANUAL_RUN_PROCESSOR_TYPES.realTime);
      if (isRealTimeProcessor) {
        setDestinationDeploymentManualRunDialog((previous) => ({
          ...previous,
          error: "",
          feedback: {
            ...(previous.feedback || createDestinationManualRunFeedbackState()),
            active: true,
            complete: true,
            success: false,
            message,
            details: []
          }
        }));
        return;
      }
      setDestinationDeploymentManualRunDialog((previous) => ({
        ...previous,
        error: message
      }));
      setConnectionResultDialog({
        title: "Manual Run",
        status: "error",
        message,
        details: []
      });
    }
  }, [
    buildWorkspaceResourceQueryParams,
    clearGlobalWarnings,
    closeDestinationDeploymentManualRunDialog,
    destinationDeploymentManualRunDialog.deployment,
    destinationDeploymentManualRunDialog.values,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    getDestinationManager,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey
  ]);

  const openWarehouseDeploymentDeployDialog = useCallback((deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    if (normalizeCompare(defaults?.managerKey) !== "warehouse" || !getId(destination) || !deployment || !deploymentId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Warehouse deploy is not available for this deployment."
      });
      return;
    }
    setWarehouseDeploymentDeployDialog({
      open: true,
      deployment,
      values: { eoids: [], includeViews: false, as: false, processorType: DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE },
      error: ""
    });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, setConfigurationActionState]);

  const closeWarehouseDeploymentDeployDialog = useCallback(() => {
    setWarehouseDeploymentDeployDialog({
      open: false,
      deployment: null,
      values: { eoids: [], includeViews: false, as: false, processorType: DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE },
      error: ""
    });
  }, []);

  const setWarehouseDeploymentDeployValues = useCallback((values) => {
    setWarehouseDeploymentDeployDialog((previous) => ({
      ...previous,
      values: values || { eoids: [], includeViews: false, as: false, processorType: DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE },
      error: ""
    }));
  }, []);

  const getDestinationPayloadWithDeploymentActive = useCallback((deployment, isActive) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentMatchKey = getDeploymentMatchKey(deployment, defaults);
    if (!getId(destination) || !defaults || !deployment || !deploymentMatchKey) return null;

    const nextDeployments = getDestinationDeployments(destination, defaults).map((currentDeployment) => {
      if (getDeploymentMatchKey(currentDeployment, defaults) !== deploymentMatchKey) return currentDeployment;
      return {
        ...currentDeployment,
        IsActive: isActive,
        isActive,
        ...(isActive ? { EnableSync: true, enableSync: true } : {})
      };
    });

    return {
      ...updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
      ...(isActive ? { EnableSync: true, enableSync: true } : {})
    };
  }, [
    destinationScreenState.defaults,
    destinationScreenState.destination
  ]);

  const setDestinationDeploymentActive = useCallback(async (deployment, isActive, actionPrefix = "destination-deployment-active") => {
    const defaults = destinationScreenState.defaults;
    const deploymentMatchKey = getDeploymentMatchKey(deployment, defaults);
    const payload = getDestinationPayloadWithDeploymentActive(deployment, isActive);
    if (!payload || !deploymentMatchKey) return null;

    return saveDestinationPayload(
      payload,
      `${actionPrefix}:${deploymentMatchKey}`,
      `Destination deployment could not be ${isActive ? "activated" : "disabled"}.`
    );
  }, [
    destinationScreenState.defaults,
    getDestinationPayloadWithDeploymentActive,
    saveDestinationPayload
  ]);

  const openWarehouseDeploymentDropDialog = useCallback((deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    if (normalizeCompare(defaults?.managerKey) !== "warehouse" || !getId(destination) || !deployment || !deploymentId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Warehouse drop is not available for this deployment."
      });
      return;
    }
    if (isRecordActive(deployment)) {
      setConfigurationActionState({
        busyAction: "",
        error: "Disable this deployment before dropping physical warehouse objects."
      });
      return;
    }
    setWarehouseDeploymentDropDialog({
      open: true,
      deployment,
      error: ""
    });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, setConfigurationActionState]);

  const closeWarehouseDeploymentDropDialog = useCallback(() => {
    setWarehouseDeploymentDropDialog({
      open: false,
      deployment: null,
      error: ""
    });
  }, []);

  const openWarehouseDeploymentCloneDialog = useCallback((deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    if (normalizeCompare(defaults?.managerKey) !== "warehouse" || !getId(destination) || !deployment || !deploymentId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Warehouse clone is not available for this deployment."
      });
      return;
    }
    setWarehouseDeploymentCloneDialog({
      open: true,
      deployment,
      values: { dbn: "", whn: "", svid: "" },
      error: ""
    });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, setConfigurationActionState]);

  const closeWarehouseDeploymentCloneDialog = useCallback(() => {
    setWarehouseDeploymentCloneDialog({
      open: false,
      deployment: null,
      values: { dbn: "", whn: "", svid: "" },
      error: ""
    });
  }, []);

  const setWarehouseDeploymentCloneValues = useCallback((values) => {
    setWarehouseDeploymentCloneDialog((previous) => ({
      ...previous,
      values: values || { dbn: "", whn: "", svid: "" },
      error: ""
    }));
  }, []);

  const runWarehouseDeploymentOperation = useCallback(async ({
    deployment,
    operationName,
    metadata,
    processorType,
    itemOverride,
    busyAction,
    title,
    successFallback,
    errorFallback,
    keepErrorInDialog,
    onSuccess
  }) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentId = getDeploymentId(deployment);
    const deploymentName = getDeploymentDisplayName(
      deployment,
      destination,
      getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination))
    );

    if (normalizeCompare(defaults?.managerKey) !== "warehouse" || !getId(destination) || !deployment || !deploymentId || !operationName) {
      throw new Error(errorFallback || "Warehouse deployment operation is not available.");
    }

    const activeDestinationMgr = getDestinationManager(defaults);
    if (!activeDestinationMgr?.RunOperation) {
      throw new Error("Warehouse operation service is not available.");
    }

    setConfigurationActionState({ busyAction, error: "" });
    const response = await activeDestinationMgr.RunOperation({
      operationName,
      item: itemOverride || destination,
      queryParams: buildWorkspaceResourceQueryParams(metadata, destination),
      isItem: true,
      isWarning: true,
      ...(processorType ? { processorType: Number(normalizeDestinationManualRunProcessorType(processorType)) } : {}),
      supressAllErrors: true
    });
    const result = getOperationResult(response);
    const success = !(result?.Success === false || result?.success === false);
    if (!success) {
      clearGlobalWarnings();
    }
    setConfigurationActionState({ busyAction: "", error: "" });
    const message = getOperationResultMessage(response, success ? `${deploymentName}: ${successFallback}` : `${deploymentName}: ${errorFallback}`);
    const details = getOperationResultDetails(response);
    if (success || !keepErrorInDialog) {
      setConnectionResultDialog?.({
        title,
        status: success ? "success" : "error",
        message,
        details
      });
    }
    setRecentLogsRefreshKey((current) => current + 1);
    if (success) {
      onSuccess?.(response);
      refreshDestinationWorkspace();
      loadCatalog();
    } else if (keepErrorInDialog) {
      const error = new Error(message);
      error.response = response;
      error.details = details;
      throw error;
    }
    return response;
  }, [
    buildWorkspaceResourceQueryParams,
    clearGlobalWarnings,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    getDestinationManager,
    loadCatalog,
    refreshDestinationWorkspace,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey
  ]);

  const confirmWarehouseDeploymentDeploy = useCallback(async () => {
    const deployment = warehouseDeploymentDeployDialog.deployment;
    const values = warehouseDeploymentDeployDialog.values || {};
    const validationMessage = validateWarehouseDeployValues(values);
    if (validationMessage) {
      setWarehouseDeploymentDeployDialog((previous) => ({ ...previous, error: validationMessage }));
      return null;
    }
    try {
      const deploymentIsActive = isRecordActive(deployment);
      let operationItemOverride = null;
      if (!deploymentIsActive) {
        await setDestinationDeploymentActive(deployment, true, "warehouse-deployment-activate-for-deploy");
        operationItemOverride = getDestinationPayloadWithDeploymentActive(deployment, true);
      }
      const response = await runWarehouseDeploymentOperation({
        deployment,
        operationName: DESTINATION_WAREHOUSE_DEPLOYMENT_OPERATIONS.deploy,
        metadata: buildWarehouseDeployMetadata({ deployment, values }),
        processorType: values?.processorType || DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE,
        itemOverride: operationItemOverride,
        busyAction: `warehouse-deployment-deploy:${getDeploymentId(deployment)}`,
        title: "Deploy Warehouse",
        successFallback: "Warehouse deploy started.",
        errorFallback: "Warehouse deploy failed.",
        keepErrorInDialog: true,
        onSuccess: closeWarehouseDeploymentDeployDialog
      });
      return response;
    } catch (deployError) {
      const message = deployError?.message || "Warehouse deploy could not be started.";
      setConfigurationActionState({ busyAction: "", error: "" });
      setWarehouseDeploymentDeployDialog((previous) => ({ ...previous, error: message }));
      return null;
    }
  }, [
    closeWarehouseDeploymentDeployDialog,
    getDestinationPayloadWithDeploymentActive,
    runWarehouseDeploymentOperation,
    setDestinationDeploymentActive,
    setConfigurationActionState,
    warehouseDeploymentDeployDialog.deployment,
    warehouseDeploymentDeployDialog.values
  ]);

  const confirmWarehouseDeploymentDrop = useCallback(async () => {
    const deployment = warehouseDeploymentDropDialog.deployment;
    try {
      const response = await runWarehouseDeploymentOperation({
        deployment,
        operationName: DESTINATION_WAREHOUSE_DEPLOYMENT_OPERATIONS.drop,
        metadata: buildWarehouseDropMetadata({ deployment }),
        processorType: DEFAULT_WAREHOUSE_DEPLOYMENT_ACTION_PROCESSOR_TYPE,
        busyAction: `warehouse-deployment-drop:${getDeploymentId(deployment)}`,
        title: "Drop Warehouse",
        successFallback: "Warehouse drop started.",
        errorFallback: "Warehouse drop failed.",
        keepErrorInDialog: true,
        onSuccess: closeWarehouseDeploymentDropDialog
      });
      return response;
    } catch (dropError) {
      const message = dropError?.message || "Warehouse drop could not be started.";
      setConfigurationActionState({ busyAction: "", error: "" });
      setWarehouseDeploymentDropDialog((previous) => ({ ...previous, error: message }));
      return null;
    }
  }, [
    closeWarehouseDeploymentDropDialog,
    runWarehouseDeploymentOperation,
    setConfigurationActionState,
    warehouseDeploymentDropDialog.deployment
  ]);

  const confirmWarehouseDeploymentClone = useCallback(async () => {
    const deployment = warehouseDeploymentCloneDialog.deployment;
    const values = warehouseDeploymentCloneDialog.values || {};
    if (!String(values.dbn || "").trim()) {
      return null;
    }
    try {
      const response = await runWarehouseDeploymentOperation({
        deployment,
        operationName: DESTINATION_WAREHOUSE_DEPLOYMENT_OPERATIONS.clone,
        metadata: buildWarehouseCloneMetadata({ deployment, values }),
        busyAction: `warehouse-deployment-clone:${getDeploymentId(deployment)}`,
        title: "Clone Warehouse Deployment",
        successFallback: "Warehouse deployment cloned.",
        errorFallback: "Warehouse deployment clone failed.",
        onSuccess: closeWarehouseDeploymentCloneDialog
      });
      return response;
    } catch (cloneError) {
      const message = cloneError?.message || "Warehouse deployment could not be cloned.";
      setConfigurationActionState({ busyAction: "", error: "" });
      setWarehouseDeploymentCloneDialog((previous) => ({ ...previous, error: message }));
      setConnectionResultDialog?.({ title: "Clone Warehouse Deployment", status: "error", message, details: cloneError?.details || [] });
      return null;
    }
  }, [
    closeWarehouseDeploymentCloneDialog,
    runWarehouseDeploymentOperation,
    setConfigurationActionState,
    setConnectionResultDialog,
    warehouseDeploymentCloneDialog.deployment,
    warehouseDeploymentCloneDialog.values
  ]);

  const openAddDestinationDeploymentPanel = useCallback(() => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    if (!getId(destination) || !defaults) {
      setConfigurationActionState({ busyAction: "", error: "No linked Destination is available for deployment setup." });
      return;
    }
    setEditDestinationDeploymentPanelData(buildDestinationDeploymentDraft(destination, defaults, getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination))));
    setEditDestinationDeploymentPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, destinationScreenState.schema, setConfigurationActionState]);

  const openEditDestinationDeploymentPanel = useCallback((deployment) => {
    if (!deployment) return;
    setEditDestinationDeploymentPanelData({
      ...deployment,
      IsNew: false
    });
    setEditDestinationDeploymentPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [setConfigurationActionState]);

  const closeEditDestinationDeploymentPanel = useCallback(() => {
    setEditDestinationDeploymentPanelOpen(false);
    setEditDestinationDeploymentPanelData(null);
  }, []);

  const findDeploymentBySelectionKey = useCallback((deployments, selectedKey, defaults) => {
    const normalizedSelectedKey = normalizeCompare(selectedKey);
    return getArray({ deployments }, "deployments").find((deployment, index) =>
      normalizeCompare(getDeploymentId(deployment)) === normalizedSelectedKey
      || normalizeCompare(getDeploymentRenderKey(deployment, defaults, index)) === normalizedSelectedKey
      || normalizeCompare(getDeploymentMatchKey(deployment, defaults)) === normalizedSelectedKey
    ) || null;
  }, []);

  const getDefaultEntityPickerDeployment = useCallback((deployments) =>
    getDefaultDeployment(deployments),
  []);

  const openAddEndpointEntityEndpointPanel = useCallback(async (deploymentSelectionKey = "") => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deployments = getDestinationDeployments(destination, defaults);
    const selectedDeployment = findDeploymentBySelectionKey(deployments, deploymentSelectionKey, defaults)
      || getDefaultDestinationDeployment(destination, defaults);
    const selectedDeploymentId = getDeploymentId(selectedDeployment);
    if (!defaults?.isEndpoint || !getId(destination) || !selectedDeployment || !selectedDeploymentId) {
      setConfigurationActionState({ busyAction: "", error: "No endpoint deployment is available for endpoint scope setup." });
      return;
    }
    const schemaScopes = getEndpointDeploymentSchemaScopes(selectedDeployment);
    const defaultSchemaScope = schemaScopes[0] || null;
    const schemaScopeIdentifier = getString(defaultSchemaScope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    const activeSchemaVersionId = getString(defaultSchemaScope, "SchemaVersionId", "schemaVersionId");
    if (!defaultSchemaScope || !schemaScopeIdentifier || !activeSchemaVersionId) {
      setConfigurationActionState({ busyAction: "", error: "An active Schema Scope is required before adding an Entity Endpoint." });
      return;
    }
    let entityObjectOptions;
    try {
      entityObjectOptions = await loadEndpointEntityObjectSelectOptions(selectedDeployment);
    } catch (loadOptionsError) {
      setConfigurationActionState({
        busyAction: "",
        error: loadOptionsError?.message || "Endpoint Entity Object options could not be loaded."
      });
      return;
    }
    const defaultEntityObjectOption = entityObjectOptions.find((option) =>
      normalizeCompare(getString(option, "FilterValue", "filterValue")) === normalizeCompare(schemaScopeIdentifier)
      && getValue(option, "Disabled", "disabled") !== true
    ) || null;
    const defaultEntityObjectId = getString(defaultEntityObjectOption, "Value", "value");
    const schemaVersionLabel = getString(defaultSchemaScope, "SchemaVersion", "schemaVersion")
      || getSchemaVersionLabel(destinationScreenState.schema, activeSchemaVersionId);
    const firstEntityObject = destinationScreenState.entityObjects.find((entityObject) =>
      normalizeCompare(getEntityObjectRecordId(entityObject)) === normalizeCompare(defaultEntityObjectId)
    ) || null;
    const firstEntityObjectId = getEntityObjectRecordId(firstEntityObject) || defaultEntityObjectId;
    setEditEndpointEntityEndpointPanelData({
      __EndpointEntityMode: "add",
      __DeploymentId: selectedDeploymentId,
      _tempKey: `endpoint-entity-${Date.now()}`,
      UniqueId: createClientGuid(),
      IsNew: true,
      IsActive: true,
      IsDeleted: false,
      SchemaDestinationDeploymentId: selectedDeploymentId,
      SchemaScopeIdentifier: schemaScopeIdentifier,
      EntitySchemaVersionId: activeSchemaVersionId,
      EntitySchemaVersion: schemaVersionLabel,
      __EndpointEntityObjectSelectionId: firstEntityObjectId,
      EntityObjectId: firstEntityObjectId,
      EntityName: "",
      EntityObjectVersion: getString(firstEntityObject, "Version", "version", "EntityObjectVersion", "entityObjectVersion"),
      ExecutionMode: normalizeCompare(getString(defaultEntityObjectOption, "Icon", "icon")) === "database" ? "2" : "1",
      EnableWarehouseNativeQuery: false
    });
    setEditEndpointEntityEndpointPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.entityObjects,
    destinationScreenState.schema,
    findDeploymentBySelectionKey,
    loadEndpointEntityObjectSelectOptions,
    setConfigurationActionState
  ]);

  const openEditEndpointEntityEndpointPanel = useCallback(async (endpointEntityEndpoint) => {
    if (!endpointEntityEndpoint) return;
    const deploymentId = getString(endpointEntityEndpoint, "__DeploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
    const deployment = findDeploymentBySelectionKey(
      getDestinationDeployments(destinationScreenState.destination, destinationScreenState.defaults),
      deploymentId,
      destinationScreenState.defaults
    );
    const resolvedDeploymentId = getDeploymentId(deployment) || deploymentId;
    const entitySchemaVersionId = getString(endpointEntityEndpoint, "EntitySchemaVersionId", "entitySchemaVersionId", "SchemaVersionId", "schemaVersionId");
    const schemaScopes = getEndpointDeploymentSchemaScopes(deployment);
    const endpointSchemaScopeIdentifier = getString(endpointEntityEndpoint, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    const matchingSchemaScope = schemaScopes.find((scope) =>
      normalizeCompare(getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier")) === normalizeCompare(endpointSchemaScopeIdentifier)
    ) || schemaScopes.find((scope) =>
      normalizeCompare(getString(scope, "SchemaVersionId", "schemaVersionId")) === normalizeCompare(entitySchemaVersionId)
    ) || schemaScopes[0] || null;
    try {
      await loadEndpointEntityObjectSelectOptions(deployment);
    } catch (loadOptionsError) {
      setConfigurationActionState({
        busyAction: "",
        error: loadOptionsError?.message || "Endpoint Entity Object options could not be loaded."
      });
      return;
    }
    setEditEndpointEntityEndpointPanelData({
      ...endpointEntityEndpoint,
      SchemaDestinationDeploymentId: resolvedDeploymentId,
      __DeploymentId: resolvedDeploymentId,
      __EndpointEntityMode: "edit",
      __EndpointEntityOriginalKey: getEndpointEntityEndpointKey(endpointEntityEndpoint),
      SchemaScopeIdentifier: getString(matchingSchemaScope, "SchemaScopeIdentifier", "schemaScopeIdentifier"),
      __EndpointEntityObjectSelectionId: getString(endpointEntityEndpoint, "EntityObjectId", "entityObjectId"),
      IsNew: false
    });
    setEditEndpointEntityEndpointPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [
    destinationScreenState.defaults,
    destinationScreenState.destination,
    findDeploymentBySelectionKey,
    loadEndpointEntityObjectSelectOptions,
    setConfigurationActionState
  ]);

  const closeEditEndpointEntityEndpointPanel = useCallback(() => {
    setEditEndpointEntityEndpointPanelOpen(false);
    setEditEndpointEntityEndpointPanelData(null);
  }, []);

  const saveEndpointEntityEndpointDetails = useCallback(async (updatedEndpointEntityEndpoint) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    if (!defaults?.isEndpoint || !getId(destination)) return undefined;

    const endpointEntityEndpointDraft = {
      ...editEndpointEntityEndpointPanelData,
      ...(updatedEndpointEntityEndpoint || {})
    };
    const currentDeployments = getDestinationDeployments(destination, defaults);
    const selectedDeploymentKey = getString(endpointEntityEndpointDraft, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "__DeploymentId");
    const selectedDeploymentIndex = currentDeployments.findIndex((deployment, index) => {
      const renderKey = getDeploymentRenderKey(deployment, defaults, index);
      return normalizeCompare(getDeploymentId(deployment)) === normalizeCompare(selectedDeploymentKey)
        || normalizeCompare(renderKey) === normalizeCompare(selectedDeploymentKey)
        || normalizeCompare(getDeploymentMatchKey(deployment, defaults)) === normalizeCompare(selectedDeploymentKey);
    });
    const selectedDeployment = selectedDeploymentIndex >= 0
      ? currentDeployments[selectedDeploymentIndex]
      : getDefaultDestinationDeployment(destination, defaults);
    const selectedDeploymentId = getDeploymentId(selectedDeployment);
    if (!selectedDeployment || !selectedDeploymentId) {
      setConfigurationActionState({ busyAction: "", error: "No endpoint deployment is available for endpoint scope setup." });
      return undefined;
    }

    const schemaScopeSelectionKey = getString(endpointEntityEndpointDraft, "SchemaScopeIdentifier", "schemaScopeIdentifier", "__SchemaScopeSelectionKey");
    const selectedSchemaScope = getEndpointDeploymentSchemaScopes(selectedDeployment).find((scope) =>
      normalizeCompare(getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier")) === normalizeCompare(schemaScopeSelectionKey)
    ) || null;
    const selectedSchemaScopeIdentifier = getString(selectedSchemaScope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    const selectedSchemaVersionId = getString(selectedSchemaScope, "SchemaVersionId", "schemaVersionId");
    if (!selectedSchemaScope || !selectedSchemaScopeIdentifier || !selectedSchemaVersionId) {
      setConfigurationActionState({ busyAction: "", error: "Select an active Schema Scope before saving the Entity Endpoint." });
      return undefined;
    }

    const entityObjectId = getString(endpointEntityEndpointDraft, "__EndpointEntityObjectSelectionId", "EntityObjectId", "entityObjectId");
    const entityObject = destinationScreenState.entityObjects.find((candidate) =>
      normalizeCompare(getEntityObjectRecordId(candidate)) === normalizeCompare(entityObjectId)
    ) || null;
    if (!entityObject || normalizeCompare(getString(entityObject, "SchemaVersionId", "schemaVersionId")) !== normalizeCompare(selectedSchemaVersionId)) {
      setConfigurationActionState({ busyAction: "", error: "Select an Entity Object from the active version of the selected Schema Scope." });
      return undefined;
    }
    const hasIsActive = endpointEntityEndpointDraft.IsActive !== undefined || endpointEntityEndpointDraft.isActive !== undefined;
    const normalizedEndpointEntityEndpoint = cleanEndpointEntityEndpointRowForSave({
      ...endpointEntityEndpointDraft,
      UniqueId: getString(endpointEntityEndpointDraft, "UniqueId", "uniqueId") || createClientGuid(),
      IsNew: false,
      IsActive: hasIsActive ? getBoolean(endpointEntityEndpointDraft, "IsActive", "isActive") : true,
      IsDeleted: getBoolean(endpointEntityEndpointDraft, "IsDeleted", "isDeleted"),
      SchemaDestinationDeploymentId: selectedDeploymentId,
      SchemaScopeIdentifier: selectedSchemaScopeIdentifier,
      DisplayName: getString(endpointEntityEndpointDraft, "DisplayName", "displayName")
        || getDisplayName(entityObject, getEntityObjectRouteName(entityObject) || "Entity Endpoint"),
      EntityObjectId: entityObjectId,
      EntityName: getString(endpointEntityEndpointDraft, "EntityName", "entityName", "EntityRouteOverride", "entityRouteOverride")
        || getEntityObjectRouteName(entityObject)
        || getDisplayName(entityObject, ""),
      EntityObjectVersion: getString(entityObject, "Version", "version", "EntityObjectVersion", "entityObjectVersion")
        || getString(endpointEntityEndpointDraft, "EntityObjectVersion", "entityObjectVersion"),
      ExecutionMode: getString(endpointEntityEndpointDraft, "ExecutionMode", "executionMode") || "1",
      EnableWarehouseNativeQuery: getBoolean(endpointEntityEndpointDraft, "EnableWarehouseNativeQuery", "enableWarehouseNativeQuery"),
      EntitySchemaVersionId: selectedSchemaVersionId,
      EntitySchemaVersion: getString(selectedSchemaScope, "SchemaVersion", "schemaVersion")
        || getSchemaVersionLabel(destinationScreenState.schema, selectedSchemaVersionId)
    });

    const currentRows = getArray(selectedDeployment, "EntityEndpoints", "entityEndpoints");
    const originalKey = getString(endpointEntityEndpointDraft, "__EndpointEntityOriginalKey")
      || getEndpointEntityEndpointKey(endpointEntityEndpointDraft);
    const nextRows = currentRows.some((row, index) => getEndpointEntityEndpointKey(row, index) === originalKey)
      ? currentRows.map((row, index) =>
        getEndpointEntityEndpointKey(row, index) === originalKey
          ? normalizedEndpointEntityEndpoint
          : cleanEndpointEntityEndpointRowForSave(row)
      )
      : [...currentRows.map(cleanEndpointEntityEndpointRowForSave), normalizedEndpointEntityEndpoint];
    const nextDeployments = currentDeployments.map((deployment, index) =>
      index === selectedDeploymentIndex || normalizeCompare(getDeploymentId(deployment)) === normalizeCompare(selectedDeploymentId)
        ? {
          ...deployment,
          EntityEndpoints: nextRows
        }
        : deployment
    );

    try {
      const response = await saveDestinationPayload(
        updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
        `endpoint-entity-save:${getEndpointEntityEndpointKey(normalizedEndpointEntityEndpoint) || "new"}`,
        "Endpoint entity scope could not be saved."
      );
      closeEditEndpointEntityEndpointPanel();
      return response;
    } catch (saveEndpointEntityError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveEndpointEntityError?.message || "Endpoint entity scope could not be saved."
      });
      showDestinationSaveError("Save Endpoint Entity Scope", saveEndpointEntityError, "Endpoint entity scope could not be saved.");
      return undefined;
    }
  }, [
    closeEditEndpointEntityEndpointPanel,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.entityObjects,
    destinationScreenState.schema,
    editEndpointEntityEndpointPanelData,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const saveDestinationDeploymentDetails = useCallback(async (updatedDeployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    if (!getId(destination) || !defaults) return undefined;

    const deploymentDraft = {
      ...editDestinationDeploymentPanelData,
      ...(updatedDeployment || {})
    };
    const targetFieldName = getDeploymentTargetFieldName(defaults);
    const stateFieldName = getDeploymentStateFieldName(defaults);
    const deploymentRoot = getDeploymentTargetValue(deploymentDraft, defaults) || "/";
    const normalizedDeploymentDraft = {
      ...deploymentDraft,
      [targetFieldName]: deploymentRoot,
      [stateFieldName]: normalizeDestinationStateValue(getString(deploymentDraft, stateFieldName, stateFieldName.charAt(0).toLowerCase() + stateFieldName.slice(1)), "1"),
      EnableSync: getBoolean(deploymentDraft, "EnableSync", "enableSync", "SyncEnable", "syncEnable"),
      DisplayName: getString(deploymentDraft, "DisplayName", "displayName")
        || getDeploymentDisplayName({ ...deploymentDraft, [targetFieldName]: deploymentRoot }, destination, getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination)))
    };
    delete normalizedDeploymentDraft.SyncEnable;
    delete normalizedDeploymentDraft.syncEnable;
    const originalDeploymentDraft = editDestinationDeploymentPanelData || {};
    const originalDeploymentId = getDeploymentId(originalDeploymentDraft);
    const originalDeploymentRenderKey = getDeploymentRenderKey(originalDeploymentDraft, defaults);
    const originalDeploymentMatchKey = getDeploymentMatchKey(originalDeploymentDraft, defaults);
    const deploymentMatchKey = getDeploymentMatchKey(normalizedDeploymentDraft, defaults);
    const currentDeployments = getDestinationDeployments(destination, defaults);
    const matchedDeploymentIndex = currentDeployments.findIndex((deployment, index) => {
      const currentDeploymentId = getDeploymentId(deployment);
      const currentDeploymentRenderKey = getDeploymentRenderKey(deployment, defaults, index);
      const currentDeploymentMatchKey = getDeploymentMatchKey(deployment, defaults);

      return (originalDeploymentId && normalizeCompare(currentDeploymentId) === normalizeCompare(originalDeploymentId))
        || (originalDeploymentRenderKey && normalizeCompare(currentDeploymentRenderKey) === normalizeCompare(originalDeploymentRenderKey))
        || (originalDeploymentMatchKey && currentDeploymentMatchKey === originalDeploymentMatchKey)
        || (deploymentMatchKey && currentDeploymentMatchKey === deploymentMatchKey);
    });
    const nextDeployments = matchedDeploymentIndex >= 0
      ? currentDeployments.map((deployment, index) =>
        index === matchedDeploymentIndex
          ? {
            ...deployment,
            ...normalizedDeploymentDraft
          }
          : deployment
      )
      : [...currentDeployments, normalizedDeploymentDraft];
    try {
      const response = await saveDestinationPayload(
        updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
        `destination-deployment-save:${deploymentMatchKey || "new"}`,
        "Destination deployment could not be saved."
      );
      closeEditDestinationDeploymentPanel();
      return response;
    } catch (saveDeploymentError) {
      const hasValidationDialog = hasBackendValidationErrors(coreState?.global?.get?.("backendValidationErrors"))
        || hasBackendValidationErrors(getBackendValidationErrorsFromResponse(saveDeploymentError?.response));
      setConfigurationActionState({
        busyAction: "",
        error: hasValidationDialog ? "" : saveDeploymentError?.message || "Destination deployment could not be saved."
      });
      showDestinationSaveError("Save Deployment", saveDeploymentError, "Destination deployment could not be saved.");
      return undefined;
    }
  }, [
    closeEditDestinationDeploymentPanel,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.schema,
    editDestinationDeploymentPanelData,
    coreState,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const cleanSchemaScopeRowsForSave = useCallback((schemaScopes) =>
    getArray({ schemaScopes }, "schemaScopes").map((scope) => {
      const { __scopeKey, __validationMessage, ...cleanScope } = scope || {};
      return cleanScope;
    }),
  []);

  const saveDestinationDeploymentSchemaScopes = useCallback(async (deployment, schemaScopes = []) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    if (!getId(destination) || !defaults || !deployment) return undefined;

    const originalDeploymentId = getDeploymentId(deployment);
    const originalDeploymentRenderKey = getDeploymentRenderKey(deployment, defaults);
    const originalDeploymentMatchKey = getDeploymentMatchKey(deployment, defaults);
    const currentDeployments = getDestinationDeployments(destination, defaults);
    const matchedDeploymentIndex = currentDeployments.findIndex((candidate, index) => {
      const currentDeploymentId = getDeploymentId(candidate);
      const currentDeploymentRenderKey = getDeploymentRenderKey(candidate, defaults, index);
      const currentDeploymentMatchKey = getDeploymentMatchKey(candidate, defaults);

      return (originalDeploymentId && normalizeCompare(currentDeploymentId) === normalizeCompare(originalDeploymentId))
        || (originalDeploymentRenderKey && normalizeCompare(currentDeploymentRenderKey) === normalizeCompare(originalDeploymentRenderKey))
        || (originalDeploymentMatchKey && currentDeploymentMatchKey === originalDeploymentMatchKey);
    });

    if (matchedDeploymentIndex < 0) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema scopes could not be saved because the deployment could not be matched."
      });
      return undefined;
    }

    const cleanedSchemaScopes = cleanSchemaScopeRowsForSave(schemaScopes);
    const nextDeployments = currentDeployments.map((candidate, index) =>
      index === matchedDeploymentIndex
        ? {
          ...candidate,
          SchemaScopes: cleanedSchemaScopes
        }
        : candidate
    );

    try {
      return await saveDestinationPayload(
        updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
        `destination-deployment-schema-scopes-save:${originalDeploymentMatchKey || originalDeploymentId || "deployment"}`,
        "Schema scopes could not be saved."
      );
    } catch (saveSchemaScopesError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveSchemaScopesError?.message || "Schema scopes could not be saved."
      });
      showDestinationSaveError("Save Schema Scopes", saveSchemaScopesError, "Schema scopes could not be saved.");
      return undefined;
    }
  }, [
    cleanSchemaScopeRowsForSave,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const deleteDestinationDeployment = useCallback((deployment) => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deploymentMatchKey = getDeploymentMatchKey(deployment, defaults);
    if (!getId(destination) || !defaults || !deploymentMatchKey) return;
    const deployments = getDestinationDeployments(destination, defaults);
    if (deployments.length <= 1) {
      setConfigurationActionState({
        busyAction: "",
        error: "At least one deployment is required for this Destination."
      });
      return;
    }
    const deploymentName = getDeploymentDisplayName(deployment, destination, getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination)));
    setDeleteDestinationDeploymentRequest({
      deployment,
      destination,
      defaults,
      schemaVersionLabel: getSchemaVersionLabel(destinationScreenState.schema, getDestinationActiveSchemaVersionId(destination)),
      deploymentName
    });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, destinationScreenState.schema, setConfigurationActionState]);

  const closeDeleteDestinationDeploymentDialog = useCallback(() => {
    setDeleteDestinationDeploymentRequest(null);
  }, []);

  const confirmDeleteDestinationDeployment = useCallback(async () => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deployment = deleteDestinationDeploymentRequest?.deployment;
    const deploymentMatchKey = getDeploymentMatchKey(deployment, defaults);
    if (!getId(destination) || !defaults || !deploymentMatchKey) return undefined;
    try {
      const nextDeployments = getDestinationDeployments(destination, defaults)
        .filter((item) => getDeploymentMatchKey(item, defaults) !== deploymentMatchKey);
      const response = await saveDestinationPayload(
        updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
        `destination-deployment-delete:${deploymentMatchKey}`,
        "Destination deployment could not be deleted."
      );
      closeDeleteDestinationDeploymentDialog();
      return response;
    } catch (deleteDeploymentError) {
      setConfigurationActionState({
        busyAction: "",
        error: deleteDeploymentError?.message || "Destination deployment could not be deleted."
      });
      showDestinationSaveError("Delete Deployment", deleteDeploymentError, "Destination deployment could not be deleted.");
      return undefined;
    }
  }, [
    closeDeleteDestinationDeploymentDialog,
    deleteDestinationDeploymentRequest,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const closeDestinationEntityPicker = useCallback(() => {
    setDestinationEntityPickerOpen(false);
    setDestinationEntityPickerSelection([]);
    setDestinationEntityPickerDeploymentId("");
  }, []);

  const openDestinationEntityPicker = useCallback((preferredDeploymentId = "") => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deployments = getDestinationDeployments(destination, defaults);
    const defaultDeployment = findDeploymentBySelectionKey(deployments, preferredDeploymentId, defaults)
      || getDefaultEntityPickerDeployment(deployments);
    setDestinationEntityPickerOpen(true);
    setDestinationEntityPickerSelection([]);
    setDestinationEntityPickerDeploymentId(defaultDeployment ? getDeploymentId(defaultDeployment) || getDeploymentRenderKey(defaultDeployment, defaults) : "");
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [destinationScreenState.defaults, destinationScreenState.destination, findDeploymentBySelectionKey, getDefaultEntityPickerDeployment, setConfigurationActionState]);

  const confirmDestinationEntityPicker = useCallback(async () => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const deployments = getDestinationDeployments(destination, defaults);
    const selectedDeployment = findDeploymentBySelectionKey(deployments, destinationEntityPickerDeploymentId, defaults)
      || getDefaultEntityPickerDeployment(deployments);
    const selectedDeploymentId = getDeploymentId(selectedDeployment);
    if (!getId(destination) || !selectedDeployment || !selectedDeploymentId || destinationEntityPickerSelection.length === 0) return;
    const existingRows = getDestinationEntityDeployments(destination);
    const existingEntityIds = new Set(existingRows
      .filter((row) =>
        normalizeCompare(getString(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "DeploymentId", "deploymentId"))
          === normalizeCompare(selectedDeploymentId)
      )
      .map((row) => normalizeCompare(getString(row, "EntityObjectId", "entityObjectId")))
      .filter(Boolean));
    const newRows = destinationScreenState.entityObjects
      .filter((entityObject) => destinationEntityPickerSelection.includes(getId(entityObject)))
      .filter((entityObject) => !existingEntityIds.has(normalizeCompare(getId(entityObject))))
      .map((entityObject) => buildDestinationEntityDeploymentDraft(entityObject, selectedDeployment, destination));
    if (newRows.length === 0) {
      closeDestinationEntityPicker();
      return;
    }
    try {
      await saveDestinationPayload(
        updateDestinationEntityDeploymentCollection(destination, [...existingRows, ...newRows], destinationScreenState.defaults),
        "destination-entity-deployment-generate",
        "Entity deployment scope could not be generated."
      );
      closeDestinationEntityPicker();
    } catch (generateError) {
      setConfigurationActionState({
        busyAction: "",
        error: generateError?.message || "Entity deployment scope could not be generated."
      });
      showDestinationSaveError("Generate Entity Deployment Scope", generateError, "Entity deployment scope could not be generated.");
    }
  }, [
    closeDestinationEntityPicker,
    destinationEntityPickerDeploymentId,
    destinationEntityPickerSelection,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    destinationScreenState.entityObjects,
    findDeploymentBySelectionKey,
    getDefaultEntityPickerDeployment,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const openEditDestinationEntityDeploymentPanel = useCallback((entityDeployment) => {
    if (!entityDeployment) return;
    setEditDestinationEntityDeploymentPanelData({
      ...entityDeployment,
      IsNew: false
    });
    setEditDestinationEntityDeploymentPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [setConfigurationActionState]);

  const closeEditDestinationEntityDeploymentPanel = useCallback(() => {
    setEditDestinationEntityDeploymentPanelOpen(false);
    setEditDestinationEntityDeploymentPanelData(null);
  }, []);

  const saveDestinationEntityDeploymentDetails = useCallback(async (updatedEntityDeployment) => {
    const destination = destinationScreenState.destination;
    if (!getId(destination)) return undefined;

    const baseEntityDeploymentDraft = {
      ...(editDestinationEntityDeploymentPanelData || {}),
      ...(updatedEntityDeployment || {})
    };
    const isWarehouseDestination = normalizeCompare(destinationScreenState.defaults?.managerKey) === "warehouse";
    const entityDeploymentDraft = {
      ...baseEntityDeploymentDraft,
      DestinationState: normalizeDestinationStateValue(getString(baseEntityDeploymentDraft, "DestinationState", "destinationState"), "1"),
      ...(isWarehouseDestination
        ? {}
        : { FilePackagingMode: normalizeFilePackagingModeValue(getString(baseEntityDeploymentDraft, "FilePackagingMode", "filePackagingMode"), "1") })
    };
    if (isWarehouseDestination) {
      delete entityDeploymentDraft.FilePackagingMode;
      delete entityDeploymentDraft.filePackagingMode;
    }
    const entityDeploymentMatchKey = getEntityDeploymentMatchKey(entityDeploymentDraft);
    const currentRows = getDestinationEntityDeployments(destination);
    const matched = currentRows.some((row) => getEntityDeploymentMatchKey(row) === entityDeploymentMatchKey);
    const nextRows = matched
      ? currentRows.map((row) =>
        getEntityDeploymentMatchKey(row) === entityDeploymentMatchKey
          ? entityDeploymentDraft
          : row
      )
      : [...currentRows, entityDeploymentDraft];
    try {
      const response = await saveDestinationPayload(
        updateDestinationEntityDeploymentCollection(destination, nextRows, destinationScreenState.defaults),
        `destination-entity-deployment-save:${entityDeploymentMatchKey || "new"}`,
        "Entity deployment scope could not be saved."
      );
      closeEditDestinationEntityDeploymentPanel();
      return response;
    } catch (saveEntityDeploymentError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveEntityDeploymentError?.message || "Entity deployment scope could not be saved."
      });
      showDestinationSaveError("Save Entity Deployment Scope", saveEntityDeploymentError, "Entity deployment scope could not be saved.");
      return undefined;
    }
  }, [
    closeEditDestinationEntityDeploymentPanel,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    editDestinationEntityDeploymentPanelData,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const deleteDestinationEntityDeployment = useCallback((entityDeployment) => {
    const entityDeploymentMatchKey = getEntityDeploymentMatchKey(entityDeployment);
    if (!entityDeployment || !entityDeploymentMatchKey || entityDeploymentMatchKey === "pair:") return;
    setDeleteDestinationEntityDeploymentRequest({ entityDeployment });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [setConfigurationActionState]);

  const closeDeleteDestinationEntityDeploymentDialog = useCallback(() => {
    setDeleteDestinationEntityDeploymentRequest(null);
  }, []);

  const confirmDeleteDestinationEntityDeployment = useCallback(async () => {
    const destination = destinationScreenState.destination;
    const entityDeployment = deleteDestinationEntityDeploymentRequest?.entityDeployment;
    const entityDeploymentMatchKey = getEntityDeploymentMatchKey(entityDeployment);
    if (!getId(destination) || !entityDeploymentMatchKey || entityDeploymentMatchKey === "pair:") return;
    try {
      const nextRows = getDestinationEntityDeployments(destination)
        .filter((row) => getEntityDeploymentMatchKey(row) !== entityDeploymentMatchKey);
      const response = await saveDestinationPayload(
        updateDestinationEntityDeploymentCollection(destination, nextRows, destinationScreenState.defaults),
        `destination-entity-deployment-delete:${entityDeploymentMatchKey}`,
        "Entity deployment scope could not be deleted."
      );
      closeDeleteDestinationEntityDeploymentDialog();
      return response;
    } catch (deleteEntityDeploymentError) {
      setConfigurationActionState({
        busyAction: "",
        error: deleteEntityDeploymentError?.message || "Entity deployment scope could not be deleted."
      });
      showDestinationSaveError("Delete Entity Deployment Scope", deleteEntityDeploymentError, "Entity deployment scope could not be deleted.");
      return undefined;
    }
  }, [
    closeDeleteDestinationEntityDeploymentDialog,
    deleteDestinationEntityDeploymentRequest,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  const deleteEndpointEntityEndpoint = useCallback((endpointEntityEndpoint) => {
    const endpointEntityEndpointKey = getEndpointEntityEndpointKey(endpointEntityEndpoint);
    if (!endpointEntityEndpoint || !endpointEntityEndpointKey) return;
    setDeleteEndpointEntityEndpointRequest({ endpointEntityEndpoint });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [setConfigurationActionState]);

  const closeDeleteEndpointEntityEndpointDialog = useCallback(() => {
    setDeleteEndpointEntityEndpointRequest(null);
  }, []);

  const confirmDeleteEndpointEntityEndpoint = useCallback(async () => {
    const destination = destinationScreenState.destination;
    const defaults = destinationScreenState.defaults;
    const endpointEntityEndpoint = deleteEndpointEntityEndpointRequest?.endpointEntityEndpoint;
    const endpointEntityEndpointKey = getEndpointEntityEndpointKey(endpointEntityEndpoint);
    const deploymentKey = getString(endpointEntityEndpoint, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId", "__DeploymentId");
    if (!defaults?.isEndpoint || !getId(destination) || !endpointEntityEndpointKey || !deploymentKey) return undefined;

    const currentDeployments = getDestinationDeployments(destination, defaults);
    const nextDeployments = currentDeployments.map((deployment, deploymentIndex) => {
      const deploymentMatches = normalizeCompare(getDeploymentId(deployment)) === normalizeCompare(deploymentKey)
        || normalizeCompare(getDeploymentRenderKey(deployment, defaults, deploymentIndex)) === normalizeCompare(deploymentKey)
        || normalizeCompare(getDeploymentMatchKey(deployment, defaults)) === normalizeCompare(deploymentKey);
      if (!deploymentMatches) return deployment;
      return {
        ...deployment,
        EntityEndpoints: getArray(deployment, "EntityEndpoints", "entityEndpoints")
          .filter((row, rowIndex) => getEndpointEntityEndpointKey(row, rowIndex) !== endpointEntityEndpointKey)
          .map(cleanEndpointEntityEndpointRowForSave)
      };
    });

    try {
      const response = await saveDestinationPayload(
        updateDestinationDeploymentCollection(destination, defaults, nextDeployments),
        `endpoint-entity-delete:${endpointEntityEndpointKey}`,
        "Endpoint entity scope could not be deleted."
      );
      closeDeleteEndpointEntityEndpointDialog();
      return response;
    } catch (deleteEndpointEntityError) {
      setConfigurationActionState({
        busyAction: "",
        error: deleteEndpointEntityError?.message || "Endpoint entity scope could not be deleted."
      });
      showDestinationSaveError("Delete Endpoint Entity Scope", deleteEndpointEntityError, "Endpoint entity scope could not be deleted.");
      return undefined;
    }
  }, [
    closeDeleteEndpointEntityEndpointDialog,
    deleteEndpointEntityEndpointRequest,
    destinationScreenState.defaults,
    destinationScreenState.destination,
    saveDestinationPayload,
    setConfigurationActionState,
    showDestinationSaveError
  ]);

  return {
    destinationScreenState: routeReadyDestinationScreenState,
    editDestinationPanelOpen,
    editDestinationPanelData,
    editDestinationPanelMetadata,
    editDestinationPanelMode,
    setEditDestinationPanelOpen,
    setEditDestinationPanelData,
    editDestinationDeploymentPanelOpen,
    editDestinationDeploymentPanelData,
    setEditDestinationDeploymentPanelOpen,
    setEditDestinationDeploymentPanelData,
    editDestinationEntityDeploymentPanelOpen,
    editDestinationEntityDeploymentPanelData,
    setEditDestinationEntityDeploymentPanelOpen,
    setEditDestinationEntityDeploymentPanelData,
    editEndpointEntityEndpointPanelOpen,
    editEndpointEntityEndpointPanelData,
    endpointEntityObjectSelectOptionsByDeployment,
    setEditEndpointEntityEndpointPanelOpen,
    setEditEndpointEntityEndpointPanelData,
    deleteDestinationDeploymentRequest,
    deleteDestinationEntityDeploymentRequest,
    deleteEndpointEntityEndpointRequest,
    destinationDeploymentManualRunDialog,
    destinationDeploymentManualRunFeedbackRef,
    destinationDeploymentManualRunStream,
    warehouseDeploymentDeployDialog,
    warehouseDeploymentDropDialog,
    warehouseDeploymentCloneDialog,
    endpointDeploymentLifecycleConfirmDialog,
    destinationEntityPickerOpen,
    destinationEntityPickerDeploymentId,
    destinationEntityPickerSelection,
    setDestinationEntityPickerDeploymentId,
    setDestinationEntityPickerSelection,
    refreshDestinationWorkspace,
    openEditDestinationPanel,
    openAddDestinationPanel,
    closeEditDestinationPanel,
    saveDestinationDetails,
    setDestinationActive,
    runDestinationConnectionCheck,
    deployEndpointDeployment,
    undeployEndpointDeployment,
    requestDeployEndpointDeployment,
    requestUndeployEndpointDeployment,
    closeEndpointDeploymentLifecycleConfirmDialog,
    confirmEndpointDeploymentLifecycleAction,
    openDestinationDeploymentManualRun,
    closeDestinationDeploymentManualRunDialog,
    setDestinationDeploymentManualRunValues,
    runDestinationDeploymentManualRun,
    openWarehouseDeploymentDeployDialog,
    closeWarehouseDeploymentDeployDialog,
    setWarehouseDeploymentDeployValues,
    confirmWarehouseDeploymentDeploy,
    openWarehouseDeploymentDropDialog,
    closeWarehouseDeploymentDropDialog,
    confirmWarehouseDeploymentDrop,
    openWarehouseDeploymentCloneDialog,
    closeWarehouseDeploymentCloneDialog,
    setWarehouseDeploymentCloneValues,
    confirmWarehouseDeploymentClone,
    openAddDestinationDeploymentPanel,
    openEditDestinationDeploymentPanel,
    closeEditDestinationDeploymentPanel,
    saveDestinationDeploymentDetails,
    saveDestinationDeploymentSchemaScopes,
    setDestinationDeploymentActive,
    deleteDestinationDeployment,
    closeDeleteDestinationDeploymentDialog,
    confirmDeleteDestinationDeployment,
    openDestinationEntityPicker,
    closeDestinationEntityPicker,
    confirmDestinationEntityPicker,
    openEditDestinationEntityDeploymentPanel,
    closeEditDestinationEntityDeploymentPanel,
    saveDestinationEntityDeploymentDetails,
    deleteDestinationEntityDeployment,
    closeDeleteDestinationEntityDeploymentDialog,
    confirmDeleteDestinationEntityDeployment,
    openAddEndpointEntityEndpointPanel,
    openEditEndpointEntityEndpointPanel,
    closeEditEndpointEntityEndpointPanel,
    saveEndpointEntityEndpointDetails,
    deleteEndpointEntityEndpoint,
    closeDeleteEndpointEntityEndpointDialog,
    confirmDeleteEndpointEntityEndpoint
  };
};
